import { formatDate, formatDateTime } from '../../shared/dateTimeFormatting';
import { useDateTimePreferences } from '../../shared/DateTimeProvider';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronDown, FileJson, KeyRound, CircleHelp, Pause, Pencil, Play, Plus, RefreshCw, Settings, Trash2, Undo2, X } from 'lucide-react';
import { InboundSchemasManagement } from './InboundSchemasManagement';
import { InboundSchemaGuide } from './InboundSchemaGuide';
import { ResourceCloneButton } from './ResourceCloneModal';
import { OutboundActionFieldMapper, OutboundActionResponseMapper, outboundSchemaFields, type OutboundPayloadVariable } from './OutboundActionFieldMapper';
import { emptyOutboundRequestMappingDocument, parseOutboundRequestMappingDocument, serializeOutboundRequestMappingDocument,
  stripOutboundRequestSchemaRequired, type OutboundRequestFieldMapping } from './outboundRequestMapping';
import { createIntegrationFilters, filterIntegrations, getTransformationDisplayName, summarizeInboundSchemaCoverage,
  type InboundSchemaCoverage, type IntegrationFilters } from './integrationFilterModel';
import { inboundSchemasApi, effectiveOutputSchema } from '../../services/inboundSchemasApi';
import './IntegrationsManagement.css';
import {
  tenantApi,
  type InboundIntegration,
  type OutboundIntegration,
  type ApiKeyInfo,
  type OutboundCredential,
  type IngestionIntegrationHealth,
  type OutboundIntegrationAction,
  type CreateOutboundIntegrationActionRequest,
} from '../../services/tenantApi';

const INGESTION_API_KEY_HEADER = import.meta.env.VITE_INGESTION_API_KEY_HEADER || 'X-Zurvit-API-Key';
const INGESTION_ENDPOINT = `${(import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '')}/api/v1/ingest`;
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const normalizeUuid = (value?: string | null) => (value ?? '').trim().replace(/[{}]/g, '').toLowerCase();
const asUuid = (value?: string | null) => {
  const normalized = normalizeUuid(value);
  return uuidPattern.test(normalized) ? normalized : null;
};

const INBOUND_PROVIDERS = [
  { value: 'Generic', label: 'Generic Webhook' },
  { value: 'Auvik', label: 'Auvik' },
  { value: 'Datadog', label: 'Datadog' },
  { value: 'Prometheus', label: 'Prometheus / Alertmanager' },
  { value: 'Zabbix', label: 'Zabbix' },
  { value: 'PagerDuty', label: 'PagerDuty' },
  { value: 'Nagios', label: 'Nagios' },
  { value: 'Grafana', label: 'Grafana' },
];

const OUTBOUND_PROVIDERS = [
  { value: 'Webhook', label: 'Webhook (Generic)' },
  { value: 'Jira', label: 'Jira' },
  { value: 'ServiceNow', label: 'ServiceNow' },
  { value: 'PagerDuty', label: 'PagerDuty' },
  { value: 'Slack', label: 'Slack' },
  { value: 'Teams', label: 'Microsoft Teams' },
];

const AUTH_TYPES = [
  { value: 'none', label: 'None' },
  { value: 'bearer', label: 'Bearer Token' },
  { value: 'basic', label: 'Basic Auth' },
  { value: 'apikey', label: 'API Key Header' },
];

type ProviderPresetSpec = {
  name: string;
  method: string;
  pathTemplate: string;
  bodyTemplate: string;
  ticketIdVariable: string;
};

const PROVIDER_PRESET_SPECS: Record<string, Record<string, ProviderPresetSpec>> = {
  generic: {
    createTicket: {
      name: 'Create Ticket',
      method: 'POST',
      pathTemplate: '/tickets',
      bodyTemplate: '{\n  "title": "{{alert.title}}",\n  "description": "{{alert.description}}",\n  "severity": "{{alert.severity}}",\n  "service": "{{alert.service}}"\n}',
      ticketIdVariable: 'id',
    },
    updateTicket: {
      name: 'Update Ticket',
      method: 'PATCH',
      pathTemplate: '/tickets/{{integration.ticketId}}',
      bodyTemplate: '{\n  "status": "{{alert.status}}",\n  "note": "Alert updated in Zurvit"\n}',
      ticketIdVariable: 'id',
    },
    closeTicket: {
      name: 'Close Ticket',
      method: 'PATCH',
      pathTemplate: '/tickets/{{integration.ticketId}}',
      bodyTemplate: '{\n  "status": "Closed",\n  "resolution": "Closed from Zurvit workflow"\n}',
      ticketIdVariable: 'id',
    },
  },
  jira: {
    createTicket: {
      name: 'Create Jira Issue',
      method: 'POST',
      pathTemplate: '/rest/api/3/issue',
      bodyTemplate: '{\n  "fields": {\n    "project": { "key": "OPS" },\n    "summary": "{{alert.title}}",\n    "description": "{{alert.description}}",\n    "issuetype": { "name": "Incident" }\n  }\n}',
      ticketIdVariable: 'key',
    },
    updateTicket: {
      name: 'Update Jira Issue',
      method: 'PUT',
      pathTemplate: '/rest/api/3/issue/{{integration.ticketId}}',
      bodyTemplate: '{\n  "fields": {\n    "description": "{{alert.description}}"\n  }\n}',
      ticketIdVariable: 'key',
    },
    closeTicket: {
      name: 'Close Jira Issue',
      method: 'POST',
      pathTemplate: '/rest/api/3/issue/{{integration.ticketId}}/transitions',
      bodyTemplate: '{\n  "transition": { "id": "31" }\n}',
      ticketIdVariable: 'key',
    },
  },
  servicenow: {
    createTicket: {
      name: 'Create ServiceNow Incident',
      method: 'POST',
      pathTemplate: '/api/now/table/incident',
      bodyTemplate: '{\n  "short_description": "{{alert.title}}",\n  "description": "{{alert.description}}",\n  "urgency": "1"\n}',
      ticketIdVariable: 'result.number',
    },
    updateTicket: {
      name: 'Update ServiceNow Incident',
      method: 'PATCH',
      pathTemplate: '/api/now/table/incident/{{integration.ticketId}}',
      bodyTemplate: '{\n  "work_notes": "Updated from Zurvit workflow"\n}',
      ticketIdVariable: 'result.number',
    },
    closeTicket: {
      name: 'Resolve ServiceNow Incident',
      method: 'PATCH',
      pathTemplate: '/api/now/table/incident/{{integration.ticketId}}',
      bodyTemplate: '{\n  "state": "6",\n  "close_notes": "Resolved from Zurvit"\n}',
      ticketIdVariable: 'result.number',
    },
  },
  pagerduty: {
    createTicket: {
      name: 'Create PagerDuty Incident',
      method: 'POST',
      pathTemplate: '/incidents',
      bodyTemplate: '{\n  "incident": {\n    "type": "incident",\n    "title": "{{alert.title}}",\n    "service": { "id": "PAGER_DUTY_SERVICE_ID", "type": "service_reference" }\n  }\n}',
      ticketIdVariable: 'incident.id',
    },
    updateTicket: {
      name: 'Update PagerDuty Incident',
      method: 'PUT',
      pathTemplate: '/incidents/{{integration.ticketId}}',
      bodyTemplate: '{\n  "incident": {\n    "type": "incident",\n    "title": "{{alert.title}}"\n  }\n}',
      ticketIdVariable: 'incident.id',
    },
    closeTicket: {
      name: 'Resolve PagerDuty Incident',
      method: 'PUT',
      pathTemplate: '/incidents/{{integration.ticketId}}',
      bodyTemplate: '{\n  "incident": {\n    "type": "incident",\n    "status": "resolved"\n  }\n}',
      ticketIdVariable: 'incident.id',
    },
  },
};

const ACTION_TEMPLATE_TOKENS: Array<{ token: string; label: string; category: 'Common' | 'Payload' | 'Steps' }> = [
  { token: '{{alert.id}}', label: 'Alert ID', category: 'Common' },
  { token: '{{alert.title}}', label: 'Alert Title', category: 'Common' },
  { token: '{{alert.description}}', label: 'Alert Description', category: 'Common' },
  { token: '{{alert.severity}}', label: 'Alert Severity', category: 'Common' },
  { token: '{{alert.status}}', label: 'Alert Status', category: 'Common' },
  { token: '{{alert.service}}', label: 'Alert Service', category: 'Common' },
  { token: '{{alert.source}}', label: 'Alert Source', category: 'Common' },
  { token: '{{alert.correlationid}}', label: 'Correlation ID', category: 'Common' },
  { token: '{{alert.externalalertid}}', label: 'External Alert ID', category: 'Common' },
  { token: '{{alert.externalticketid}}', label: 'External Ticket ID', category: 'Common' },
  { token: '{{notification.id}}', label: 'Notification ID', category: 'Common' },
  { token: '{{notification.title}}', label: 'Notification Title', category: 'Common' },
  { token: '{{notification.description}}', label: 'Notification Description', category: 'Common' },
  { token: '{{notification.severity}}', label: 'Notification Severity', category: 'Common' },
  { token: '{{notification.status}}', label: 'Notification Status', category: 'Common' },
  { token: '{{notification.service}}', label: 'Notification Service', category: 'Common' },
  { token: '{{notification.source}}', label: 'Notification Source', category: 'Common' },
  { token: '{{event.id}}', label: 'Event ID', category: 'Common' },
  { token: '{{event.type}}', label: 'Event Type', category: 'Common' },
  { token: '{{alert.data.normalisedPayload}}', label: 'Alert Workflow Payload', category: 'Payload' },
  { token: '{{notification.data.normalisedPayload}}', label: 'Notification Workflow Payload', category: 'Payload' },
  { token: '{{event.data.normalisedPayload}}', label: 'Event Workflow Payload', category: 'Payload' },
  { token: '{{integration.ticketId}}', label: 'Associated Ticket ID', category: 'Steps' },
  { token: '{{step.1.responseStatusCode}}', label: 'Step Response Status', category: 'Steps' },
  { token: '{{step.1.responseBody}}', label: 'Step Response Body', category: 'Steps' },
  { token: '{{step.1.success}}', label: 'Step Success', category: 'Steps' },
];

const ACTION_SAMPLE_TOKEN_VALUES: Record<string, string> = {
  'alert.id': '11111111-2222-3333-4444-555555555555',
  'alert.title': 'Router down - branch office',
  'alert.description': 'WAN gateway is unreachable',
  'alert.severity': 'Critical',
  'alert.status': 'Open',
  'alert.service': 'Network',
  'alert.source': 'Auvik',
  'alert.correlationid': 'corr-123456',
  'alert.externalalertid': 'auvik-alert-987',
  'alert.externalticketid': 'D365-4321',
  'integration.ticketId': 'D365-4321',
  'notification.id': 'notification-123',
  'notification.title': 'Router down - branch office',
  'notification.description': 'WAN gateway is unreachable',
  'notification.severity': 'Critical',
  'notification.status': 'Open',
  'notification.service': 'Network',
  'notification.source': 'Auvik',
  'event.id': 'event-123',
  'event.type': 'NotificationReceived',
  'step.1.responseStatusCode': '201',
  'step.1.responseBody': '{"ticketId":"D365-4321"}',
  'step.1.success': 'true',
};

const ACTION_TOKEN_PATTERN = /\{\{\s*([a-zA-Z0-9_.-]+)\s*(?:\|[^}]*)?\}\}/g;

const flattenSamplePayloadPaths = (input: unknown, prefix = ''): string[] => {
  if (input == null) return [];

  if (Array.isArray(input)) {
    if (input.length === 0) return [];
    return flattenSamplePayloadPaths(input[0], `${prefix}[0]`);
  }

  if (typeof input !== 'object') {
    return prefix ? [prefix] : [];
  }

  const entries = Object.entries(input as Record<string, unknown>);
  if (entries.length === 0) return prefix ? [prefix] : [];

  const result: string[] = [];
  for (const [key, value] of entries) {
    const nextPrefix = prefix ? `${prefix}.${key}` : key;
    result.push(...flattenSamplePayloadPaths(value, nextPrefix));
  }

  return result;
};

const extractNormalizedPayloadTokens = (input: unknown): string[] => {
  const roots = [
    'alert.data.normalisedPayload',
    'notification.data.normalisedPayload',
    'event.data.normalisedPayload',
  ];
  return Array.from(new Set(flattenSamplePayloadPaths(input)
    .map(path => path.replace(/\[(\d+)\]/g, '.$1'))
    .flatMap(path => roots.map(root => `{{${root}.${path}}}`))));
};

const deriveActiveNormalizedPayloadTokens = (integration: InboundIntegration, schemaJson: string): string[] => {
  const roots = integration.nonAlert
    ? ['notification.data.normalisedPayload', 'event.data.normalisedPayload']
    : ['alert.data.normalisedPayload', 'event.data.normalisedPayload'];
  return outboundSchemaFields(schemaJson).flatMap(field => {
    const segments = field.pointer.split('/').slice(1).map(segment => segment.replace(/~1/g, '/').replace(/~0/g, '~'));
    const path = segments.join('.');
    if (!path || !/^[A-Za-z0-9_.-]+$/.test(path)) return [];
    return roots.map(root => `{{${root}.${path}}}`);
  });
};

const deriveQualifiedNormalizedPayloadVariables = (
  integration: InboundIntegration,
  schemaJson: string,
): OutboundPayloadVariable[] => {
  const sourceName = `${integration.name} (${integration.nonAlert ? 'Notification' : 'Alert'})`;
  const root = `inbound.${integration.inboundIntegrationId}.data.normalisedPayload`;
  return outboundSchemaFields(schemaJson).flatMap(field => {
    const segments = field.pointer.split('/').slice(1).map(segment => segment.replace(/~1/g, '/').replace(/~0/g, '~'));
    const nestedPath = segments.join('.');
    if (!nestedPath || !/^[A-Za-z0-9_.-]+$/.test(nestedPath)) return [];
    return [{ path: `${root}.${nestedPath}`, sourceName, type: field.type, integrationId: integration.inboundIntegrationId }];
  });
};

const normalizeLegacyTokenSyntax = (template: string): string => {
  if (!template) return template;
  return template.replace(/\{([a-zA-Z][a-zA-Z0-9_.-]*)\}/g, (_m, group: string) => `{{${group}}}`);
};

const applySampleTokenPreview = (template: string, tokenValues: Record<string, string>): string => {
  if (!template) return template;
  return template.replace(/\{\{\s*([a-zA-Z0-9_.-]+)\s*(?:\|[^}]*)?\}\}/g, (full, tokenKey: string) => {
    const value = tokenValues[tokenKey];
    return value != null && value !== '' ? value : full;
  });
};

const createDefaultOutboundActionForm = (): CreateOutboundIntegrationActionRequest => ({
  name: '',
  actionKey: 'createTicket',
  method: 'POST',
  pathTemplate: '',
  bodyTemplate: '{}',
  isDeliveryCapable: false,
  deliveryInputs: [],
  requestSchemaJson: '',
  requestMappingsJson: serializeOutboundRequestMappingDocument(emptyOutboundRequestMappingDocument()),
  responseSchemaJson: '',
  responseMappingsJson: '{}',
  headers: {},
  ticketIdVariable: '',
  isEnabled: true,
  sortOrder: 0,
});

const formatJsonForEditing = (value?: string): string => {
  if (!value?.trim()) return value || '';
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
};

const normalizeRequestSchemaForEditing = (value?: string): string => {
  if (!value?.trim()) return value || '';
  try { return stripOutboundRequestSchemaRequired(value); }
  catch { return value; }
};

const normalizeRequestMappingsForEditing = (value?: string): string => {
  try { return serializeOutboundRequestMappingDocument(parseOutboundRequestMappingDocument(value || '')); }
  catch { return serializeOutboundRequestMappingDocument(emptyOutboundRequestMappingDocument()); }
};

const toActionForm = (action: OutboundIntegrationAction): CreateOutboundIntegrationActionRequest => ({
  name: action.name,
  actionKey: action.actionKey,
  method: action.method,
  pathTemplate: action.pathTemplate || '',
  bodyTemplate: action.bodyTemplate || '',
  isDeliveryCapable: action.isDeliveryCapable === true,
  deliveryInputs: action.deliveryInputs || [],
  requestSchemaJson: normalizeRequestSchemaForEditing(action.requestSchemaJson),
  requestMappingsJson: normalizeRequestMappingsForEditing(action.requestMappingsJson),
  responseSchemaJson: formatJsonForEditing(action.responseSchemaJson),
  responseMappingsJson: action.responseMappingsJson || '{}',
  headers: action.headers || {},
  ticketIdVariable: action.ticketIdVariable || '',
  isEnabled: action.isEnabled,
  sortOrder: action.sortOrder,
});

const emptyRequestMappingsJson = () => serializeOutboundRequestMappingDocument(emptyOutboundRequestMappingDocument());

const buildActionDraftSnapshot = (form: CreateOutboundIntegrationActionRequest): string => {
  const sortedHeaders = Object.entries(form.headers || {})
    .sort(([a], [b]) => a.localeCompare(b))
    .reduce<Record<string, string>>((acc, [k, v]) => {
      acc[k] = normalizeLegacyTokenSyntax(v || '');
      return acc;
    }, {});

  return JSON.stringify({
    name: (form.name || '').trim(),
    actionKey: (form.actionKey || '').trim(),
    method: (form.method || '').trim().toUpperCase(),
    pathTemplate: normalizeLegacyTokenSyntax(form.pathTemplate || ''),
    bodyTemplate: normalizeLegacyTokenSyntax(form.bodyTemplate || ''),
    isDeliveryCapable: form.isDeliveryCapable === true,
    deliveryInputs: form.deliveryInputs || [],
    requestSchemaJson: form.requestSchemaJson || '',
    requestMappingsJson: form.requestMappingsJson || emptyRequestMappingsJson(),
    responseSchemaJson: form.responseSchemaJson || '',
    responseMappingsJson: form.responseMappingsJson || '{}',
    headers: sortedHeaders,
    ticketIdVariable: (form.ticketIdVariable || '').trim(),
    isEnabled: form.isEnabled ?? true,
    sortOrder: form.sortOrder || 0,
  });
};

const parseStringMap = (value?: string): Record<string, string> => {
  if (!value?.trim()) return {};
  try {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed).filter((entry): entry is [string, string] => typeof entry[0] === 'string' && typeof entry[1] === 'string'),
    );
  } catch {
    return {};
  }
};

type RequestFieldMapping = OutboundRequestFieldMapping;

const parseRequestFieldMappings = (value?: string): Record<string, RequestFieldMapping> => {
  try { return parseOutboundRequestMappingDocument(value || '').mappings; }
  catch { return {}; }
};

const hasRequestMappingContent = (value?: string): boolean => {
  try {
    const document = parseOutboundRequestMappingDocument(value || '');
    return Object.keys(document.mappings).length > 0 || document.requiredTargets.length > 0;
  } catch { return false; }
};

const variablePathPattern = /^[A-Za-z][A-Za-z0-9_.-]*$/;

const getRequestMappingValidation = (value?: string): { count: number; error: string | null } => {
  const raw = value?.trim() || serializeOutboundRequestMappingDocument(emptyOutboundRequestMappingDocument());
  try {
    const document = parseOutboundRequestMappingDocument(raw);
    const parsed = document.mappings;
    let count = 0;
    for (const [target, source] of Object.entries(parsed)) {
      if (!target.startsWith('/') || target.length > 1024)
        return { count: 0, error: 'Each request mapping target must be a JSON Pointer no longer than 1024 characters.' };
      if ('sources' in source) {
        if (source.combine !== 'join' || source.sources.length < 1 || source.sources.length > 32
          || source.sources.some(item => item.length > 1024 || item.startsWith('secret.') || !variablePathPattern.test(item))
          || new Set(source.sources).size !== source.sources.length
          || (source.separator !== undefined && source.separator.length > 256)) {
          return { count: 0, error: 'Join mappings require 1 to 32 unique non-secret variable paths and a separator of at most 256 characters.' };
        }
        count++;
        continue;
      }
      if ('value' in source) {
        if (source.value.length > 1024) return { count: 0, error: 'Static request values must be strings no longer than 1024 characters.' };
        count++;
        continue;
      }
      if (source.source !== '' && (source.source.length > 1024 || source.source.startsWith('secret.') || !variablePathPattern.test(source.source))) {
        return { count: 0, error: 'Each request mapping must map a JSON Pointer target to a non-secret variable path or static string.' };
      }
      count++;
    }
    if (document.requiredTargets.some(target => !target.startsWith('/') || target.length > 1024))
      return { count: 0, error: 'Required request targets must be JSON Pointers no longer than 1024 characters.' };
    return { count, error: null };
  } catch {
    return { count: 0, error: 'Request mappings must be valid JSON.' };
  }
};

const getResponseMappingValidation = (value?: string): { count: number; error: string | null } => {
  const raw = value?.trim() || '{}';
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { count: 0, error: 'Response mappings must be a JSON object.' };
    }

    let count = 0;
    for (const [target, source] of Object.entries(parsed as Record<string, unknown>)) {
      if (target.toLowerCase() === 'integration.ticketid') {
        return { count: 0, error: 'integration.ticketId is managed by Ticket ID Variable and cannot be a response-mapping destination.' };
      }
      const validJsonPath = typeof source === 'string' && (source === '$' || (source.startsWith('$.') &&
        source.slice(2).split('.').every(segment => {
          if (!segment) return false;
          const bracket = segment.indexOf('[');
          if (bracket < 0) return true;
          return bracket > 0 && /^\d+\]$/.test(segment.slice(bracket + 1));
        })));
      const validArtifactTarget = !target.startsWith('artifact.') ||
        /^artifact\.(json|text|link|field|image)\.[A-Za-z][A-Za-z0-9_.-]*$/i.test(target);
      if (!variablePathPattern.test(target) || target.startsWith('secret.') || typeof source !== 'string' ||
        source.length > 1024 || !validArtifactTarget || (!source.startsWith('/') && !validJsonPath)) {
        return { count: 0, error: target.startsWith('artifact.') && !validArtifactTarget
          ? 'Artifact destinations must use artifact.<json|text|link|field|image>.<name>.'
          : 'Each response mapping must map a non-secret variable destination to a JSON Pointer or JSONPath source.' };
      }
      count++;
    }
    return { count, error: null };
  } catch {
    return { count: 0, error: 'Response mappings must be valid JSON.' };
  }
};

const formatJsonDocument = (value: string): string => JSON.stringify(JSON.parse(value || '{}'), null, 2);

const extractActionTokens = (...templates: Array<string | undefined>): string[] => {
  const found = new Set<string>();
  templates
    .filter((t): t is string => typeof t === 'string' && t.length > 0)
    .forEach((template) => {
      const normalized = normalizeLegacyTokenSyntax(template);
      const matches = normalized.matchAll(ACTION_TOKEN_PATTERN);
      for (const match of matches) {
        const token = (match[1] || '').trim();
        if (token) {
          found.add(token);
        }
      }
    });
  return Array.from(found).sort((a, b) => a.localeCompare(b));
};

const getProviderParityHints = (
  provider: string,
  action: OutboundIntegrationAction,
  tokens: string[],
): { status: 'good' | 'warn'; messages: string[] } => {
  const normalizedProvider = (provider || '').toLowerCase();
  const messages: string[] = [];

  const needsTicketLink = action.actionKey === 'updateTicket' || action.actionKey === 'closeTicket';
  if (needsTicketLink && !tokens.includes('integration.ticketId')) {
    messages.push('Lifecycle actions should include {{integration.ticketId}}.');
  }

  const hasTitleToken = tokens.includes('alert.title') || tokens.includes('alert.data.title');
  const hasDescriptionToken = tokens.includes('alert.description') || tokens.includes('alert.data.details');

  if (action.actionKey === 'createTicket' && (!hasTitleToken || !hasDescriptionToken)) {
    messages.push('Create actions should include title/description alert context tokens.');
  }

  if (normalizedProvider === 'jira') {
    if (!tokens.some(t => t.startsWith('integration.fields.summary') || t.startsWith('integration.fields.description'))) {
      messages.push('Jira parity: map integration.fields.summary/description for issue payload consistency.');
    }
  } else if (normalizedProvider === 'servicenow') {
    if (!tokens.some(t => t.startsWith('integration.fields.short_description') || t.startsWith('integration.fields.description'))) {
      messages.push('ServiceNow parity: map short_description/description fields for incident payload consistency.');
    }
  } else if (normalizedProvider === 'pagerduty') {
    if (!tokens.some(t => t.startsWith('integration.fields.routing_key') || t.startsWith('integration.fields.event_action'))) {
      messages.push('PagerDuty parity: include routing_key/event_action integration fields when using Events API payloads.');
    }
  } else if (normalizedProvider === 'webhook') {
    if (tokens.length === 0) {
      messages.push('Webhook parity: include alert tokens so payloads carry actionable incident context.');
    }
  }

  return {
    status: messages.length === 0 ? 'good' : 'warn',
    messages,
  };
};

const pointerEscape = (value: string): string => value.replace(/~/g, '~0').replace(/\//g, '~1');

const inferSchemaFromBodyTemplate = (bodyTemplate: string): string => {
  try {
    const parsed = JSON.parse(bodyTemplate);
    const walk = (value: unknown): unknown => {
      if (Array.isArray(value)) {
        const schema: Record<string, unknown> = { type: 'array' };
        if (value.length > 0) schema.items = walk(value[0]);
        return schema;
      }
      if (value && typeof value === 'object') {
        const properties: Record<string, unknown> = {};
        for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
          properties[key] = walk(child);
        }
        return { type: 'object', properties };
      }

      if (typeof value === 'number') return { type: 'number' };
      if (typeof value === 'boolean') return { type: 'boolean' };
      if (value === null) return { type: 'null' };
      return { type: 'string' };
    };

    return JSON.stringify(walk(parsed));
  } catch {
    return '{"type":"object","properties":{}}';
  }
};

const inferSchemaFromResponseSample = (sampleResponseJson: string): string => {
  const schema = JSON.parse(inferSchemaFromBodyTemplate(sampleResponseJson)) as Record<string, unknown>;
  const makeOptional = (node: unknown) => {
    if (!node || typeof node !== 'object' || Array.isArray(node)) return;
    const schemaNode = node as Record<string, unknown>;
    delete schemaNode.required;
    if (schemaNode.properties && typeof schemaNode.properties === 'object' && !Array.isArray(schemaNode.properties)) {
      Object.values(schemaNode.properties as Record<string, unknown>).forEach(makeOptional);
    }
    if (schemaNode.items) makeOptional(schemaNode.items);
  };
  makeOptional(schema);
  return JSON.stringify(schema);
};

const buildMappedRequestPreview = (
  bodyTemplate: string,
  requestSchemaJson: string,
  requestMappingsJson: string,
  tokenValues: Record<string, string>,
): string => {
  try {
    const schemaJson = requestSchemaJson.trim() || inferSchemaFromBodyTemplate(bodyTemplate || '{}');
    const allowedTargets = new Set(outboundSchemaFields(schemaJson).map(field => field.pointer));
    const mappingDocument = parseOutboundRequestMappingDocument(requestMappingsJson || '');
    const mappings = mappingDocument.mappings;
    const selectedMappings = new Map(Object.entries(mappings)
      .filter(([target]) => target.startsWith('/') && allowedTargets.has(target)));
    const selectedTargets = new Set([...selectedMappings.keys(), ...mappingDocument.requiredTargets.filter(target => allowedTargets.has(target))]);
    const sampleBody = applySampleTokenPreview(normalizeLegacyTokenSyntax(bodyTemplate || '{}'), tokenValues);
    const sourceBody = JSON.parse(sampleBody) as unknown;

    const mappedSourceValue = (source: string): string => source.startsWith('static:')
      ? source.slice('static:'.length)
      : tokenValues[source] ?? `{{${source}}}`;
    const truncate = (value: string | undefined, maxLength?: number) =>
      value !== undefined && maxLength !== undefined && value.length > maxLength ? value.slice(0, maxLength) : value;
    const mappedValue = (mapping: RequestFieldMapping): string | undefined => {
      if ('source' in mapping) return mapping.source ? truncate(mappedSourceValue(mapping.source), mapping.maxLength) : undefined;
      if ('value' in mapping) return mapping.value;
      return mapping.sources.length ? truncate(mapping.sources.map(mappedSourceValue).join(mapping.separator ?? ' '), mapping.maxLength) : undefined;
    };

    const project = (value: unknown, pointer: string): unknown => {
      const mapping = selectedMappings.get(pointer);
      if (mapping !== undefined) {
        const mapped = mappedValue(mapping);
        if (mapped !== undefined) return mapped;
      }

      if (Array.isArray(value)) {
        const projected = value.map((item, index) => project(item, `${pointer}/${index}`));
        return projected.some(item => item !== undefined) ? projected : undefined;
      }

      if (value && typeof value === 'object') {
        const projected = Object.fromEntries(Object.entries(value as Record<string, unknown>)
          .map(([key, child]) => [`${pointer}/${pointerEscape(key)}`, project(child, `${pointer}/${pointerEscape(key)}`)] as const)
          .filter(([, child]) => child !== undefined)
          .map(([childPointer, child]) => [childPointer.slice(childPointer.lastIndexOf('/') + 1).replace(/~1/g, '/').replace(/~0/g, '~'), child]));
        return Object.keys(projected).length ? projected : undefined;
      }

      return selectedTargets.has(pointer) ? value : undefined;
    };

    const preview = project(sourceBody, '');
    return JSON.stringify(preview ?? {}, null, 2);
  } catch {
    return 'Enter valid Body Template JSON and request schema to preview mapped fields.';
  }
};

const inferRequestMappingsFromBodyTemplate = (bodyTemplate: string): string => {
  try {
    const parsed = JSON.parse(bodyTemplate);
    const mappings: Record<string, OutboundRequestFieldMapping> = {};
    const collect = (value: unknown, pointer: string) => {
      if (Array.isArray(value)) {
        value.forEach((item, index) => collect(item, `${pointer}/${index}`));
        return;
      }
      if (value && typeof value === 'object') {
        for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
          collect(child, `${pointer}/${pointerEscape(key)}`);
        }
        return;
      }

      if (typeof value !== 'string') return;
      const match = value.match(/^\{\{\s*([A-Za-z0-9_.-]+)\s*\}\}$/);
      if (match && !match[1].startsWith('secret.')) {
        mappings[pointer] = { source: match[1] };
      }
    };

    collect(parsed, '');
    return serializeOutboundRequestMappingDocument({ version: 1, mappings, requiredTargets: [] });
  } catch {
    return emptyRequestMappingsJson();
  }
};

const buildResponseSchemaFromTicketVariable = (ticketIdVariable: string): string => {
  const segments = (ticketIdVariable || '').split('.').filter(Boolean);
  if (segments.length === 0) return '{"type":"object","properties":{}}';

  let schema: Record<string, unknown> = { type: 'string' };
  for (let index = segments.length - 1; index >= 0; index -= 1) {
    schema = {
      type: 'object',
      properties: { [segments[index]]: schema },
      required: [segments[index]],
    };
  }
  return JSON.stringify(schema);
};

const buildResponseMappingsFromTicketVariable = (actionKey: string, ticketIdVariable: string): string => {
  void actionKey;
  void ticketIdVariable;
  return '{}';
};

const removeTicketIdResponseMapping = (value?: string): string => {
  const mappings = parseStringMap(value);
  const filtered = Object.fromEntries(Object.entries(mappings)
    .filter(([target]) => target.toLowerCase() !== 'integration.ticketid'));
  return Object.keys(filtered).length === Object.keys(mappings).length
    ? value || '{}'
    : JSON.stringify(filtered);
};

const getProviderPresetSpec = (provider: string, actionKey: string): ProviderPresetSpec | null => {
  const normalizedProvider = (provider || '').trim().toLowerCase();
  const normalizedActionKey = (actionKey || '').trim();
  const providerBucket = PROVIDER_PRESET_SPECS[normalizedProvider] || PROVIDER_PRESET_SPECS.generic;
  return providerBucket[normalizedActionKey] || null;
};

const buildProviderPresetDefaults = (provider: string, actionKey: string): Partial<CreateOutboundIntegrationActionRequest> | null => {
  const preset = getProviderPresetSpec(provider, actionKey);
  if (!preset) return null;

  return {
    name: preset.name,
    method: preset.method,
    pathTemplate: preset.pathTemplate,
    bodyTemplate: preset.bodyTemplate,
    ticketIdVariable: preset.ticketIdVariable,
    requestSchemaJson: inferSchemaFromBodyTemplate(preset.bodyTemplate),
    requestMappingsJson: inferRequestMappingsFromBodyTemplate(preset.bodyTemplate),
    responseSchemaJson: buildResponseSchemaFromTicketVariable(preset.ticketIdVariable),
    responseMappingsJson: buildResponseMappingsFromTicketVariable(actionKey, preset.ticketIdVariable),
  };
};

const schemaHasFields = (value?: string): boolean => {
  if (!value?.trim()) return false;
  try {
    const parsed = JSON.parse(value) as { properties?: Record<string, unknown> };
    return !!parsed && typeof parsed === 'object' && !!parsed.properties
      && typeof parsed.properties === 'object' && Object.keys(parsed.properties).length > 0;
  } catch {
    return false;
  }
};

const prepareActionFormForEdit = (
  action: OutboundIntegrationAction,
  provider: string,
): CreateOutboundIntegrationActionRequest => {
  const form = {
    ...toActionForm(action),
    responseMappingsJson: removeTicketIdResponseMapping(action.responseMappingsJson),
  };
  if (!action.isSystemPreset) return form;

  const defaults = buildProviderPresetDefaults(provider, action.actionKey);
  if (!defaults) return form;

  return {
    ...form,
    ticketIdVariable: form.ticketIdVariable || defaults.ticketIdVariable || '',
    requestSchemaJson: schemaHasFields(form.requestSchemaJson) ? form.requestSchemaJson : defaults.requestSchemaJson || form.requestSchemaJson,
    requestMappingsJson: hasRequestMappingContent(form.requestMappingsJson)
      ? form.requestMappingsJson : defaults.requestMappingsJson || form.requestMappingsJson,
    responseSchemaJson: schemaHasFields(form.responseSchemaJson) ? form.responseSchemaJson : defaults.responseSchemaJson || form.responseSchemaJson,
    responseMappingsJson: Object.keys(parseStringMap(form.responseMappingsJson)).length > 0
      ? form.responseMappingsJson : defaults.responseMappingsJson || form.responseMappingsJson,
  };
};

export const __outboundActionWizardTesting = {
  extractNormalizedPayloadTokens,
  deriveActiveNormalizedPayloadTokens,
  deriveQualifiedNormalizedPayloadVariables,
  getRequestMappingValidation,
  getResponseMappingValidation,
  formatJsonDocument,
  buildProviderPresetDefaults,
  inferSchemaFromBodyTemplate,
  inferSchemaFromResponseSample,
  buildMappedRequestPreview,
  inferRequestMappingsFromBodyTemplate,
  buildResponseSchemaFromTicketVariable,
  buildResponseMappingsFromTicketVariable,
  removeTicketIdResponseMapping,
  prepareActionFormForEdit,
};

interface IntegrationsManagementProps {
  initialIntegrationId?: string;
  isTenantAdmin?: boolean;
  canCloneInbound?: boolean;
  canCloneOutbound?: boolean;
}

type IntegrationDeleteTarget =
  | { kind: 'inbound'; integration: InboundIntegration; action: 'archive' | 'permanent' }
  | { kind: 'outbound'; integration: OutboundIntegration; action: 'archive' | 'permanent' };

export const IntegrationsManagement: React.FC<IntegrationsManagementProps> = ({
  initialIntegrationId: _initialIntegrationId, isTenantAdmin = false,
  canCloneInbound = isTenantAdmin, canCloneOutbound = isTenantAdmin,
}) => {
  const [schemaLinkError, setSchemaLinkError] = useState<string | null>(null);
  useDateTimePreferences();
  const [inbound, setInbound] = useState<InboundIntegration[]>([]);
  const [archivedInbound, setArchivedInbound] = useState<InboundIntegration[]>([]);
  const [outbound, setOutbound] = useState<OutboundIntegration[]>([]);
  const [archivedOutbound, setArchivedOutbound] = useState<OutboundIntegration[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'inbound' | 'outbound' | 'advanced'>('inbound');
  const [inboundHowToOpen, setInboundHowToOpen] = useState(false);
  const [outboundHowToOpen, setOutboundHowToOpen] = useState(false);
  const [inboundFilters, setInboundFilters] = useState(createIntegrationFilters);
  const [outboundFilters, setOutboundFilters] = useState(createIntegrationFilters);
  const filteredInbound = filterIntegrations(inbound, 'inbound', inboundFilters);
  const filteredOutbound = filterIntegrations(outbound, 'outbound', outboundFilters);
  const inboundRetainedReferenceCount = (item: InboundIntegration) =>
    (item.receiptCount ?? 0) + (item.schemaVersionCount ?? 0) + (item.workflowReferenceCount ?? 0);
  const filters = activeTab === 'inbound' ? inboundFilters : outboundFilters;
  const setFilters = activeTab === 'inbound' ? setInboundFilters : setOutboundFilters;
  const resetFilters = () => setFilters(createIntegrationFilters());
  const isHowToOpen = activeTab === 'inbound' ? inboundHowToOpen : outboundHowToOpen;
  const toggleHowTo = () => {
    if (activeTab === 'inbound') {
      setInboundHowToOpen(current => !current);
      return;
    }
    setOutboundHowToOpen(current => !current);
  };

  const renderFilters = () => (
    <div className="integrations-filter-toolbar" aria-label={`${activeTab} integration filters`}>
      <label className="integrations-search">
        <span>Search</span>
        <input type="search" value={filters.search} placeholder="Search integrations"
          onChange={(event) => setFilters({ ...filters, search: event.target.value })} />
      </label>
      {activeTab === 'inbound' && (
        <label>
          <span>Integration type</span>
          <select value={filters.type} onChange={(event) => setFilters({ ...filters, type: event.target.value as IntegrationFilters['type'] })}>
            <option value="all">All</option>
            <option value="alert">Alert</option>
            <option value="notification">Notification</option>
          </select>
        </label>
      )}
      <label>
        <span>Status</span>
        <select value={filters.status} onChange={(event) => setFilters({ ...filters, status: event.target.value as IntegrationFilters['status'] })}>
          <option value="all">All</option>
          <option value="enabled">Enabled</option>
          <option value="disabled">Disabled</option>
        </select>
      </label>
      <span className="integrations-match-count" role="status">
        {activeTab === 'inbound' ? filteredInbound.length : filteredOutbound.length} matching of {activeTab === 'inbound' ? inbound.length : outbound.length}
      </span>
      <button className="btn-secondary btn-sm" onClick={resetFilters}>Reset filters</button>
    </div>
  );
  const [healthByIntegrationId, setHealthByIntegrationId] = useState<Map<string, IngestionIntegrationHealth>>(new Map());
  const [schemaCoverageByIntegrationId, setSchemaCoverageByIntegrationId] = useState<Map<string, InboundSchemaCoverage>>(new Map());
  const [ingestionOverallStatus, setIngestionOverallStatus] = useState<'healthy' | 'degraded' | 'warning' | 'critical'>('healthy');

  // Modal states
  const [showCreateInbound, setShowCreateInbound] = useState(false);
  const [showCreateOutbound, setShowCreateOutbound] = useState(false);
  const [showApiKeyCreated, setShowApiKeyCreated] = useState<string | null>(null);
  const [selectedInbound, setSelectedInbound] = useState<InboundIntegration | null>(null);
  const [editingInbound, setEditingInbound] = useState<InboundIntegration | null>(null);
  const [schemasInbound, setSchemasInbound] = useState<InboundIntegration | null>(null);
  const pendingSchemaFocusReturn = useRef<string | null>(null);
  const [editingOutbound, setEditingOutbound] = useState<OutboundIntegration | null>(null);
  const [selectedOutboundForActions, setSelectedOutboundForActions] = useState<OutboundIntegration | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<IntegrationDeleteTarget | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    if (loading || !pendingSchemaFocusReturn.current) return;
    const integrationId = pendingSchemaFocusReturn.current;
    pendingSchemaFocusReturn.current = null;
    const trigger = Array.from(document.querySelectorAll<HTMLButtonElement>('[data-inbound-schema-trigger]'))
      .find(button => button.dataset.inboundSchemaTrigger === integrationId);
    trigger?.focus();
  }, [inbound, loading]);

  const loadAll = async (openSchemaFromQuery = true, refreshOnly = false) => {
    if (!refreshOnly) setLoading(true);
    try {
      const [inData, outData, health, archivedInboundData, archivedOutboundData] = await Promise.all([
        tenantApi.getInboundIntegrations(),
        tenantApi.getOutboundIntegrations(),
        tenantApi.getIngestionHealth().catch(() => ({ integrations: [], overallStatus: 'healthy' as const })),
        tenantApi.getDeletedInboundIntegrations().catch(() => []),
        tenantApi.getDeletedOutboundIntegrations().catch(() => []),
      ]);
      const inboundRows = Array.isArray(inData) ? inData : [];
      setInbound(inboundRows);
      setArchivedInbound(Array.isArray(archivedInboundData) ? archivedInboundData : []);
      setOutbound(Array.isArray(outData) ? outData : []);
      setArchivedOutbound(Array.isArray(archivedOutboundData) ? archivedOutboundData : []);
      const schemaCoverageEntries = await Promise.all(inboundRows.map(async (item): Promise<[string, InboundSchemaCoverage]> => {
        try {
          const registry = await inboundSchemasApi.list(item.inboundIntegrationId);
          return [item.inboundIntegrationId.toLowerCase(), summarizeInboundSchemaCoverage(registry.definitions, registry.settings)];
        } catch {
          return [item.inboundIntegrationId.toLowerCase(), { status: 'unavailable' }];
        }
      }));
      setSchemaCoverageByIntegrationId(new Map(schemaCoverageEntries));
      const query = new URLSearchParams(window.location.search);
      const queryIntegrationId = normalizeUuid(query.get('integrationId'));
      if (openSchemaFromQuery && query.get('view') === 'schemas' && Array.isArray(inData)) {
        const target = inData.find(item => normalizeUuid(item.inboundIntegrationId) === queryIntegrationId);
        const invalidReceipt = query.has('receiptId') && !asUuid(query.get('receiptId'));
        const message = !isTenantAdmin ? 'Administrator access is required to resolve this schema issue.'
          : !queryIntegrationId || !target ? 'The linked integration is unavailable in this tenant.'
          : invalidReceipt ? 'The linked receipt ID is invalid.'
          : null;
        setSchemaLinkError(message);
        setSchemasInbound(message ? null : target ?? null);
      }
      const map = new Map<string, IngestionIntegrationHealth>();
      (health.integrations || []).forEach((entry) => {
        map.set(String(entry.integrationId || '').toLowerCase(), entry);
      });
      setHealthByIntegrationId(map);
      setIngestionOverallStatus(health.overallStatus || 'healthy');
    } catch (err) {
      console.error('Failed to load integrations:', err);
      setSchemaLinkError('Failed to load integrations. Reload to retry opening the schema issue.');
      if (refreshOnly) throw err;
    } finally {
      setLoading(false);
    }
  };

  const formatLastSeen = (iso?: string | null) => {
    if (!iso) return 'No payloads received yet';
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return 'Unknown';
    return formatDateTime(iso);
  };

  // ── Inbound handlers ────────────────────────────────────────────────────

  const handleDeleteInbound = (integration: InboundIntegration) => {
    setDeleteError(null);
    setDeleteTarget({ kind: 'inbound', integration, action: inboundRetainedReferenceCount(integration) > 0 ? 'archive' : 'permanent' });
  };

  const handleRestoreInbound = async (item: InboundIntegration) => {
    try {
      await tenantApi.restoreInboundIntegration(item.inboundIntegrationId);
      await loadAll();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleRestoreOutbound = async (item: OutboundIntegration) => {
    try {
      await tenantApi.restoreOutboundIntegration(item.outboundIntegrationId);
      await loadAll();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleToggleInbound = async (item: InboundIntegration) => {
    try {
      await tenantApi.updateInboundIntegration(item.inboundIntegrationId, { isEnabled: !item.isEnabled });
      loadAll();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleCreateInboundKey = async (id: string, reference: string) => {
    const result = await tenantApi.createInboundIntegrationKey(id, reference);
    setSelectedInbound(null);
    setShowApiKeyCreated(result.key);
  };

  // ── Outbound handlers ───────────────────────────────────────────────────

  const handleDeleteOutbound = (integration: OutboundIntegration) => {
    setDeleteError(null);
    setDeleteTarget({ kind: 'outbound', integration, action: (integration.retainedReferenceCount ?? 0) > 0 ? 'archive' : 'permanent' });
  };

  const confirmDeleteIntegration = async () => {
    if (!deleteTarget || deleteSubmitting) return;
    setDeleteSubmitting(true);
    setDeleteError(null);
    try {
      if (deleteTarget.kind === 'inbound') {
        await tenantApi.deleteInboundIntegration(deleteTarget.integration.inboundIntegrationId);
      } else {
        await tenantApi.deleteOutboundIntegration(deleteTarget.integration.outboundIntegrationId);
      }
      setDeleteTarget(null);
      await loadAll();
    } catch (err: any) {
      setDeleteError(err?.message || 'The integration could not be deleted.');
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const handleToggleOutbound = async (item: OutboundIntegration) => {
    try {
      await tenantApi.updateOutboundIntegration(item.outboundIntegrationId, { isEnabled: !item.isEnabled });
      loadAll();
    } catch (err: any) {
      alert(err.message);
    }
  };

  return (
    <div className="management-section integrations-management">
      {schemaLinkError && <div className="error-message" role="alert">{schemaLinkError}</div>}
      <div className="section-header">
        <h2>Integrations</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {activeTab !== 'advanced' && <>
            <button className="btn-secondary" onClick={toggleHowTo}>
              {isHowToOpen
                ? `Hide ${activeTab === 'inbound' ? 'Inbound' : 'Outbound'} Guide`
                : `How ${activeTab === 'inbound' ? 'Inbound' : 'Outbound'} Integrations Work`}
            </button>
            <button className="btn-primary" onClick={() => activeTab === 'inbound' ? setShowCreateInbound(true) : setShowCreateOutbound(true)}>
              {activeTab === 'inbound' ? '+ Add Inbound' : '+ Add Outbound'}
            </button>
          </>}
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: '0', borderBottom: '2px solid var(--color-border)', marginBottom: '1.5rem' }}>
        <button
          onClick={() => setActiveTab('inbound')}
          style={{
            padding: '0.75rem 1.5rem',
            background: activeTab === 'inbound' ? 'var(--color-primary-light)' : 'transparent',
            color: activeTab === 'inbound' ? 'var(--color-primary)' : 'var(--color-text-secondary)',
            border: 'none',
            borderBottom: activeTab === 'inbound' ? '2px solid var(--color-primary)' : '2px solid transparent',
            cursor: 'pointer',
            fontWeight: activeTab === 'inbound' ? 600 : 400,
            fontSize: '0.95rem',
            marginBottom: '-2px',
          }}
        >
          Inbound ({inbound.length})
        </button>
        <button
          onClick={() => setActiveTab('outbound')}
          style={{
            padding: '0.75rem 1.5rem',
            background: activeTab === 'outbound' ? 'var(--color-primary-light)' : 'transparent',
            color: activeTab === 'outbound' ? 'var(--color-primary)' : 'var(--color-text-secondary)',
            border: 'none',
            borderBottom: activeTab === 'outbound' ? '2px solid var(--color-primary)' : '2px solid transparent',
            cursor: 'pointer',
            fontWeight: activeTab === 'outbound' ? 600 : 400,
            fontSize: '0.95rem',
            marginBottom: '-2px',
          }}
        >
          Outbound ({outbound.length})
        </button>
        <button
          onClick={() => setActiveTab('advanced')}
          style={{
            padding: '0.75rem 1.5rem',
            background: activeTab === 'advanced' ? 'var(--color-primary-light)' : 'transparent',
            color: activeTab === 'advanced' ? 'var(--color-primary)' : 'var(--color-text-secondary)',
            border: 'none',
            borderBottom: activeTab === 'advanced' ? '2px solid var(--color-primary)' : '2px solid transparent',
            cursor: 'pointer',
            fontWeight: activeTab === 'advanced' ? 600 : 400,
            fontSize: '0.95rem',
            marginBottom: '-2px',
          }}
        >
          Advanced ({archivedInbound.length + archivedOutbound.length})
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading integrations...</div>
      ) : activeTab === 'inbound' ? (
        /* ═══ INBOUND TAB ═══ */
        <>
          <InboundHowItWorks open={inboundHowToOpen} hideToggle />

          <div className="data-table" style={{ padding: '1rem', marginBottom: '1rem' }}>
            <h3 style={{ marginTop: 0 }}>Integration Setup</h3>
            <div className="help-text" style={{ marginBottom: 8 }}>
              Copy the endpoint and add your integration key in the header below.
            </div>
            <div className="code-block">
              <label>Webhook Endpoint</label>
              <code>{INGESTION_ENDPOINT}</code>
              <button
                className="copy-btn"
                onClick={() => { navigator.clipboard.writeText(INGESTION_ENDPOINT); alert('Copied!'); }}
              >Copy</button>
            </div>
            <div className="code-block" style={{ marginTop: '0.5rem' }}>
              <label>Auth Header</label>
              <code>{INGESTION_API_KEY_HEADER}: {'<your-api-key>'}</code>
            </div>
            <div className="help-text" style={{ marginTop: 8 }}>
              Ingestion health: <strong>{ingestionOverallStatus}</strong>
            </div>
          </div>

          <div className="integrations-list-heading" style={{ marginBottom: '1rem' }}>
            <p style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
              Configure a source below, then generate an API key to start receiving alerts.
            </p>
          </div>

          {renderFilters()}

          {inbound.length === 0 ? (
            <div className="empty-state">
              <p>No inbound integrations configured.</p>
              <button className="btn-primary" onClick={() => setShowCreateInbound(true)}>
                Create Your First Inbound Integration
              </button>
            </div>
          ) : (
            <div className="integrations-table-scroll" role="region" aria-label="Inbound integrations" tabIndex={0}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Provider</th>
                  <th>Type</th>
                  <th>Schema Mapping</th>
                  <th>API Keys</th>
                  <th>Health</th>
                  <th>Last Payload Received</th>
                  <th className="status-column">Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInbound.length === 0 && (
                  <tr><td colSpan={9} className="integrations-no-matches">
                    <div className="integrations-no-matches-content">
                    <p>No inbound integrations match these filters.</p>
                    <button className="btn-secondary btn-sm" onClick={resetFilters}>Reset filters</button>
                    </div>
                  </td></tr>
                )}
                {filteredInbound.map((item) => {
                  const integrationId = String(item.inboundIntegrationId || '').toLowerCase();
                  const health = healthByIntegrationId.get(integrationId);
                  const schemaCoverage = schemaCoverageByIntegrationId.get(integrationId);
                  return (
                  <tr key={item.inboundIntegrationId}>
                    <td style={{ fontWeight: 500 }}>{item.name}</td>
                    <td>
                      <span style={{
                        background: 'var(--color-primary-light)',
                        color: 'var(--color-primary)',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '0.85rem',
                      }}>
                        {item.provider}
                      </span>
                    </td>
                    <td><span className={`integration-type-chip ${item.nonAlert ? 'integration-type-notification' : 'integration-type-alert'}`}>
                      {item.nonAlert ? 'Notification' : 'Alert'}
                    </span></td>
                    <td className="schema-coverage-cell">
                      {schemaCoverage?.status === 'unavailable' ? (
                        <span className="schema-coverage-unavailable">Schema status unavailable</span>
                      ) : schemaCoverage ? (
                        <div className="schema-coverage-lines" title={schemaCoverage.payloadMode === 'Direct'
                          ? `Input: ${schemaCoverage.directInputName || 'Not selected'}; payload unchanged`
                          : `${schemaCoverage.mappedInputShapes} saved mappings; output ${schemaCoverage.outputShapeConfigured ? 'configured' : 'not configured'}`}>
                          <span className={(schemaCoverage.payloadMode === 'Direct' ? !schemaCoverage.directInputName : schemaCoverage.mappedInputShapes === 0) ? 'schema-coverage-missing' : ''}>
                            {schemaCoverage.payloadMode === 'Direct'
                              ? `Direct: ${schemaCoverage.directInputName ? schemaCoverage.directInputEnabled ? 'Enabled' : 'Disabled' : 'Not configured'}`
                              : `Normalise: ${schemaCoverage.enabledInputShapes}/${schemaCoverage.mappedInputShapes} enabled`}
                          </span>
                          <span title={!schemaCoverage.strictEnabled ? getTransformationDisplayName(item.transformationType) : undefined}>
                            {schemaCoverage.strictEnabled ? 'Strict' : 'Legacy fallback'}
                          </span>
                        </div>
                      ) : <span>Checking schema shapes...</span>}
                    </td>
                    <td>{item.keyCount || 0}</td>
                    <td>
                      <span className={`status-badge ${health?.status === 'critical' || health?.status === 'warning' ? 'inactive' : 'active'}`}>
                        {health?.status || 'healthy'}
                      </span>
                    </td>
                    <td style={{ color: 'var(--color-text-secondary)' }}>
                      {formatLastSeen(health?.lastSuccess || null)}
                    </td>
                    <td className="status-column">
                      <span className={`status-badge ${item.isEnabled ? 'enabled' : 'disabled'}`}>
                        {item.isEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                     <td>
                       <div className="workflow-actions-row">
                         <button
                           type="button"
                           className="btn-icon api-key-icon-action"
                           onClick={() => setSelectedInbound(item)}
                           title="Manage API Keys"
                           aria-label={`Manage API keys for ${item.name}`}
                         >
                           <KeyRound size={16} />
                         </button>
                         <button
                           type="button"
                           className="btn-icon integration-action-edit"
                           onClick={() => setEditingInbound(item)}
                           title="Edit integration"
                           aria-label={`Edit ${item.name}`}
                         >
                           <Pencil size={16} />
                         </button>
                         {canCloneInbound && <ResourceCloneButton source={{ rootKind: 'inbound', rootId: item.inboundIntegrationId, name: item.name }}
                           onCreated={() => loadAll(false, true)} onOpen={async result => {
                             const created = (await tenantApi.getInboundIntegrations()).find(row => row.inboundIntegrationId === result.rootId);
                             if (!created) throw new Error('The cloned inbound integration is unavailable. Reload the list to retry.');
                             setEditingInbound(created);
                           }} />}
                         <button type="button" className="btn-icon integration-action-schema" data-inbound-schema-trigger={item.inboundIntegrationId}
                           onClick={() => setSchemasInbound(item)}
                           title="Manage inbound schemas" aria-label={`Manage inbound schemas for ${item.name}`}>
                           <FileJson size={16} />
                         </button>
                         <button
                           type="button"
                           className="btn-icon integration-action-toggle"
                           onClick={() => handleToggleInbound(item)}
                           title={item.isEnabled ? 'Disable' : 'Enable'}
                           aria-label={`${item.isEnabled ? 'Disable' : 'Enable'} ${item.name}`}
                         >
                           {item.isEnabled ? <Pause size={16} /> : <Play size={16} />}
                         </button>
                         <button
                           type="button"
                           className="btn-icon integration-action-delete"
                           onClick={() => handleDeleteInbound(item)}
                           title={inboundRetainedReferenceCount(item) > 0 ? 'Archive' : 'Delete permanently'}
                           aria-label={`${inboundRetainedReferenceCount(item) > 0 ? 'Archive' : 'Delete permanently'} ${item.name}`}
                         >
                           <Trash2 size={16} />
                         </button>
                        </div>
                      </td>
                   </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}

        </>
      ) : activeTab === 'outbound' ? (
        /* ═══ OUTBOUND TAB ═══ */
        <>
          <OutboundHowItWorks open={outboundHowToOpen} hideToggle />

          <div className="integrations-list-heading" style={{ marginBottom: '1rem' }}>
            <p style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
              Connect an external system below to automatically receive alert events from Zurvit.
            </p>
          </div>

          {renderFilters()}

          {outbound.length === 0 ? (
            <div className="empty-state">
              <p>No outbound integrations configured.</p>
              <button className="btn-primary" onClick={() => setShowCreateOutbound(true)}>
                Create Your First Outbound Integration
              </button>
            </div>
          ) : (
            <div className="integrations-table-scroll" role="region" aria-label="Outbound integrations" tabIndex={0}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Provider</th>
                  <th>Base URL</th>
                  <th>Auth</th>
                  <th className="status-column">Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredOutbound.length === 0 && (
                  <tr><td colSpan={6} className="integrations-no-matches">
                    <div className="integrations-no-matches-content">
                    <p>No outbound integrations match these filters.</p>
                    <button className="btn-secondary btn-sm" onClick={resetFilters}>Reset filters</button>
                    </div>
                  </td></tr>
                )}
                {filteredOutbound.map((item) => (
                  <tr key={item.outboundIntegrationId}>
                    <td style={{ fontWeight: 500 }}>{item.name}</td>
                    <td>
                      <span style={{
                        background: 'var(--color-primary-light)',
                        color: 'var(--color-primary)',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '0.85rem',
                      }}>
                        {item.provider}
                      </span>
                    </td>
                    <td style={{ color: 'var(--color-text-secondary)', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.baseUrl || '-'}
                    </td>
                    <td style={{ color: 'var(--color-text-secondary)' }}>{item.authType}</td>
                    <td className="status-column">
                      <span className={`status-badge ${item.isEnabled ? 'enabled' : 'disabled'}`}>
                        {item.isEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                     <td>
                       <div className="workflow-actions-row">
                         <button
                           type="button"
                           className="btn-icon integration-action-edit"
                           onClick={() => setEditingOutbound(item)}
                           title="Edit integration"
                           aria-label={`Edit ${item.name}`}
                         >
                           <Pencil size={16} />
                         </button>
                         {canCloneOutbound && <ResourceCloneButton source={{ rootKind: 'outbound', rootId: item.outboundIntegrationId, name: item.name }}
                           onCreated={() => loadAll(false, true)} onOpen={async result => {
                             const created = (await tenantApi.getOutboundIntegrations()).find(row => row.outboundIntegrationId === result.rootId);
                             if (!created) throw new Error('The cloned outbound integration is unavailable. Reload the list to retry.');
                             setEditingOutbound(created);
                           }} />}
                         <button
                           type="button"
                           className="btn-icon integration-action-manage"
                           onClick={() => setSelectedOutboundForActions(item)}
                           title="Manage integration actions"
                           aria-label={`Manage actions for ${item.name}`}
                         >
                           <Settings size={16} />
                         </button>
                         <button
                           type="button"
                           className="btn-icon integration-action-toggle"
                           onClick={() => handleToggleOutbound(item)}
                           title={item.isEnabled ? 'Disable' : 'Enable'}
                           aria-label={`${item.isEnabled ? 'Disable' : 'Enable'} ${item.name}`}
                         >
                           {item.isEnabled ? <Pause size={16} /> : <Play size={16} />}
                         </button>
                         <button
                           type="button"
                           className="btn-icon integration-action-delete"
                           onClick={() => handleDeleteOutbound(item)}
                           title={(item.retainedReferenceCount ?? 0) > 0 ? 'Archive' : 'Delete permanently'}
                           aria-label={`${(item.retainedReferenceCount ?? 0) > 0 ? 'Archive' : 'Delete permanently'} ${item.name}`}
                         >
                           <Trash2 size={16} />
                         </button>
                       </div>
                     </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="guide-callout" style={{ marginBottom: '1rem' }}>
            <strong>Archived integrations</strong>
            <p className="help-text" style={{ marginBottom: 0 }}>
              Archived integrations are disabled and hidden from the active lists. Restore returns them to their original list but keeps them disabled.
              Retained receipts, published schema versions, alert associations, and workflow references prevent permanent deletion.
            </p>
          </div>
          {archivedInbound.length === 0 && archivedOutbound.length === 0 ? (
            <div className="empty-state"><p>No archived integrations.</p></div>
          ) : <>
            <h3>Inbound</h3>
            {archivedInbound.length === 0 ? <p className="help-text">No archived inbound integrations.</p> : (
            <div className="integrations-table-scroll" role="region" aria-label="Archived inbound integrations" tabIndex={0}>
              <table className="data-table">
                <thead>
                  <tr><th>Name</th><th>Provider</th><th>Type</th><th>Archived</th><th>Receipts</th><th>Schema versions</th><th>Workflows</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {archivedInbound.map(item => (
                    <tr key={item.inboundIntegrationId}>
                      <td style={{ fontWeight: 500 }}>{item.name}</td>
                      <td>{item.provider}</td>
                      <td>{item.nonAlert ? 'Notification' : 'Alert'}</td>
                      <td>{item.deletedAt ? formatDateTime(item.deletedAt) : '-'}</td>
                      <td>{item.receiptCount ?? 0}</td>
                      <td>{item.schemaVersionCount ?? 0}</td>
                      <td>{item.workflowReferenceCount ?? 0}</td>
                      <td>
                        <div className="workflow-actions-row">
                          <button type="button" className="btn-icon integration-action-restore"
                            onClick={() => handleRestoreInbound(item)}
                            title="Restore integration" aria-label={`Restore ${item.name}`}>
                            <Undo2 size={16} />
                          </button>
                          {inboundRetainedReferenceCount(item) === 0 && <button type="button" className="btn-icon"
                            onClick={() => handleDeleteInbound(item)}
                            title="Delete permanently" aria-label={`Delete ${item.name} permanently`}>
                            <Trash2 size={16} />
                          </button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
            <h3 style={{ marginTop: '1.5rem' }}>Outbound</h3>
            {archivedOutbound.length === 0 ? <p className="help-text">No archived outbound integrations.</p> : (
              <div className="integrations-table-scroll" role="region" aria-label="Archived outbound integrations" tabIndex={0}>
                <table className="data-table">
                  <thead><tr><th>Name</th><th>Provider</th><th>Archived</th><th>Retained references</th><th>Actions</th></tr></thead>
                  <tbody>
                    {archivedOutbound.map(item => (
                      <tr key={item.outboundIntegrationId}>
                        <td style={{ fontWeight: 500 }}>{item.name}</td>
                        <td>{item.provider}</td>
                        <td>{item.deletedAt ? formatDateTime(item.deletedAt) : '-'}</td>
                        <td>{item.retainedReferenceCount ?? 0}</td>
                        <td><div className="workflow-actions-row">
                          <button type="button" className="btn-icon integration-action-restore" onClick={() => void handleRestoreOutbound(item)}
                            title="Restore integration" aria-label={`Restore ${item.name}`}><Undo2 size={16} /></button>
                          {(item.retainedReferenceCount ?? 0) === 0 && <button type="button" className="btn-icon"
                            onClick={() => handleDeleteOutbound(item)} title="Delete permanently"
                            aria-label={`Delete ${item.name} permanently`}>&#x1F5D1;&#xFE0F;</button>}
                        </div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>}
        </>
      )}

      {/* ═══ MODALS ═══ */}

      {showCreateInbound && (
        <CreateInboundModal
          onClose={() => setShowCreateInbound(false)}
          onCreated={(created) => {
            setSchemasInbound(created);
            void loadAll();
          }}
        />
      )}

      {showCreateOutbound && (
        <CreateOutboundModal
          onClose={() => setShowCreateOutbound(false)}
          onCreated={loadAll}
        />
      )}

      {showApiKeyCreated && (
        <ApiKeyCreatedModal
          apiKey={showApiKeyCreated}
          onClose={() => { setShowApiKeyCreated(null); loadAll(); }}
        />
      )}

      {selectedInbound && (
        <InboundApiKeysModal
          integration={selectedInbound}
          onClose={() => setSelectedInbound(null)}
          onCreateKey={(reference) => handleCreateInboundKey(selectedInbound.inboundIntegrationId, reference)}
          onDeleteKey={(keyId) => tenantApi.deleteInboundIntegrationKey(selectedInbound.inboundIntegrationId, keyId)}
          onRestoreKey={(keyId) => tenantApi.restoreInboundIntegrationKey(selectedInbound.inboundIntegrationId, keyId)}
          onPurgeKey={(keyId) => tenantApi.purgeInboundIntegrationKey(selectedInbound.inboundIntegrationId, keyId)}
        />
      )}

      {editingInbound && (
        <EditInboundModal
          integration={editingInbound}
          onClose={() => setEditingInbound(null)}
          onSaved={() => { setEditingInbound(null); loadAll(); }}
        />
      )}

      {schemasInbound && (
        <InboundSchemasManagement key={schemasInbound.inboundIntegrationId} integration={schemasInbound} isTenantAdmin={isTenantAdmin}
          initialReceiptId={(() => {
            const query = new URLSearchParams(window.location.search);
            return normalizeUuid(schemasInbound.inboundIntegrationId) === normalizeUuid(query.get('integrationId'))
              ? asUuid(query.get('receiptId')) ?? undefined
              : undefined;
          })()}
          onClose={() => { pendingSchemaFocusReturn.current = schemasInbound.inboundIntegrationId; setSchemasInbound(null); void loadAll(false); }} />
      )}

      {deleteTarget && (
        <div className="modal-overlay" onMouseDown={event => { if (event.target === event.currentTarget && !deleteSubmitting) setDeleteTarget(null); }}>
          <div className="modal modal-sm" role="alertdialog" aria-modal="true" aria-labelledby="integration-delete-title"
            onClick={event => event.stopPropagation()}>
            <h3 id="integration-delete-title">
              {deleteTarget.action === 'archive' ? 'Archive integration' : 'Permanently delete integration'}
            </h3>
            <p>
              {deleteTarget.kind === 'inbound'
                ? deleteTarget.action === 'archive'
                  ? `Archive ${deleteTarget.integration.name}? It will be disabled and removed from Inbound. ${deleteTarget.integration.receiptCount ?? 0} receipt(s), ${deleteTarget.integration.schemaVersionCount ?? 0} published schema version(s), and ${deleteTarget.integration.workflowReferenceCount ?? 0} workflow reference(s) will be preserved. Restore it from Advanced.`
                  : `Permanently delete ${deleteTarget.integration.name}? It has no retained receipts, published schema versions, or workflow references; its draft schemas and API keys will also be deleted.`
                : deleteTarget.action === 'archive'
                  ? `Archive ${deleteTarget.integration.name}? It will be disabled and removed from Outbound. Its ${deleteTarget.integration.retainedReferenceCount ?? 0} retained reference(s) will be preserved. Restore it from Advanced.`
                  : `Permanently delete ${deleteTarget.integration.name}? It has no retained alert associations or workflow-step references.`}
            </p>
            {deleteError && <div className="error-message" role="alert">{deleteError}</div>}
            <div className="form-actions">
              <button type="button" className="btn-secondary" disabled={deleteSubmitting}
                onClick={() => { setDeleteTarget(null); setDeleteError(null); }}>Cancel</button>
              <button type="button" className="btn-danger" disabled={deleteSubmitting} onClick={() => void confirmDeleteIntegration()}>
                {deleteSubmitting ? 'Working…' : deleteTarget.action === 'archive' ? 'Archive' : 'Delete permanently'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editingOutbound && (
        <EditOutboundModal
          integration={editingOutbound}
          onClose={() => setEditingOutbound(null)}
          onSaved={() => { setEditingOutbound(null); loadAll(); }}
        />
      )}

      {selectedOutboundForActions && (
        <OutboundActionsModal
          integration={selectedOutboundForActions}
          inboundIntegrations={inbound}
          onClose={() => setSelectedOutboundForActions(null)}
        />
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// Create Inbound Integration Modal
// ══════════════════════════════════════════════════════════════════════════════

export const CreateInboundModal: React.FC<{
  onClose: () => void;
  onCreated: (created: InboundIntegration) => void;
}> = ({ onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [provider, setProvider] = useState('Generic');
  const [nonAlert, setNonAlert] = useState(false);
  const [payloadMode, setPayloadMode] = useState<'Direct' | 'Normalise'>('Direct');
  const [modeHelpOpen, setModeHelpOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const created = await tenantApi.createInboundIntegration({
        name,
        provider,
        nonAlert,
        payloadMode,
      });
      onCreated(created);
      onClose();
    } catch (err: any) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <h3>Create Inbound Integration</h3>
        <form onSubmit={handleSubmit}>
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label>Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="e.g., Auvik Production Alerts"
            />
          </div>

          <div className="form-group">
            <label>Provider</label>
            <select value={provider} onChange={(e) => setProvider(e.target.value)}>
              {INBOUND_PROVIDERS.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="create-inbound-type">Integration Type</label>
            <select id="create-inbound-type" value={nonAlert ? 'notification' : 'alert'} onChange={(event) => setNonAlert(event.target.value === 'notification')}>
              <option value="alert">Alert</option>
              <option value="notification">Notification</option>
            </select>
          </div>

          <div className="form-group integration-payload-mode">
            <span>Payload handling</span>
            <div className="integration-payload-mode-controls">
              <label htmlFor="create-inbound-payload-mode">Direct (passthrough)
                <input id="create-inbound-payload-mode" type="checkbox" role="switch" aria-label="Normalise payload"
                  checked={payloadMode === 'Normalise'} disabled={submitting}
                  onChange={event => setPayloadMode(event.target.checked ? 'Normalise' : 'Direct')} />
                Normalise (transform)
              </label>
              <button type="button" aria-label="Payload handling help" title="Payload handling help"
                aria-expanded={modeHelpOpen} aria-controls="create-inbound-mode-help" onClick={() => setModeHelpOpen(!modeHelpOpen)}><CircleHelp size={16} /></button>
            </div>
            {modeHelpOpen && <div id="create-inbound-mode-help" role="note" aria-label="Payload handling information">
              <p>Direct keeps the complete received JSON unchanged after it matches the selected input schema. Runtime bindings read fields without modifying the payload. New Direct integrations start disabled with strict processing on.</p>
              <p>Normalise transforms matching inputs into a shared output contract. Its mappings, output and runtime bindings remain saved when switching modes; earlier events retain their frozen snapshot.</p>
            </div>}
          </div>

          <div className="form-actions sticky-modal-actions">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary" disabled={submitting || !name.trim()}>
              {submitting ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// Create Outbound Integration Modal
// ══════════════════════════════════════════════════════════════════════════════

const CreateOutboundModal: React.FC<{
  onClose: () => void;
  onCreated: () => void;
}> = ({ onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [provider, setProvider] = useState('Webhook');
  const [baseUrl, setBaseUrl] = useState('');
  const [authType, setAuthType] = useState('none');
  // per-type credential fields
  const [bearerToken, setBearerToken] = useState('');
  const [basicUsername, setBasicUsername] = useState('');
  const [basicPassword, setBasicPassword] = useState('');
  const [apiKeyHeaderName, setApiKeyHeaderName] = useState('');
  const [apiKeyHeaderValue, setApiKeyHeaderValue] = useState('');
  const [configJson, setConfigJson] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const buildCredential = (): OutboundCredential | null => {
    if (authType === 'bearer' && bearerToken.trim()) {
      return { authType: 'bearer', token: bearerToken.trim() };
    }
    if (authType === 'basic' && (basicUsername.trim() || basicPassword.trim())) {
      return { authType: 'basic', username: basicUsername.trim(), password: basicPassword.trim() };
    }
    if (authType === 'apikey' && apiKeyHeaderName.trim() && apiKeyHeaderValue.trim()) {
      return { authType: 'apikey', headerName: apiKeyHeaderName.trim(), headerValue: apiKeyHeaderValue.trim() };
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    if (configJson.trim()) {
      try {
        JSON.parse(configJson);
      } catch {
        setError('Invalid JSON in configuration');
        setSubmitting(false);
        return;
      }
    }

    try {
      const created = await tenantApi.createOutboundIntegration({
        name,
        provider,
        baseUrl: baseUrl || undefined,
        authType,
        configJson: configJson || undefined,
      });

      const credential = buildCredential();
      if (credential && created?.outboundIntegrationId) {
        await tenantApi.storeOutboundSecret(created.outboundIntegrationId, credential);
      }

      onCreated();
      onClose();
    } catch (err: any) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <h3>Create Outbound Integration</h3>
        <p style={{ color: 'var(--color-text-secondary)', marginBottom: '1rem' }}>
          Outbound integrations push data to external systems like ticketing or chat tools.
        </p>
        <form onSubmit={handleSubmit}>
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label>Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="e.g., Jira Production Project"
            />
          </div>

          <div className="form-group">
            <label>Provider *</label>
            <select value={provider} onChange={(e) => setProvider(e.target.value)}>
              {OUTBOUND_PROVIDERS.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Base URL</label>
            <input
              type="url"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="e.g., https://your-instance.atlassian.net"
            />
          </div>

          <div className="form-group">
            <label>Authentication</label>
            <select value={authType} onChange={(e) => { setAuthType(e.target.value); }}>
              {AUTH_TYPES.map(a => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </select>
          </div>

          {authType === 'bearer' && (
            <div className="form-group">
              <label>Token</label>
              <input
                type="password"
                value={bearerToken}
                onChange={(e) => setBearerToken(e.target.value)}
                placeholder="Bearer token — stored in Key Vault"
                autoComplete="new-password"
              />
              <p className="help-text">Sent as <code>Authorization: Bearer &lt;token&gt;</code>. Stored in Azure Key Vault.</p>
            </div>
          )}

          {authType === 'basic' && (
            <>
              <div className="form-group">
                <label>Username</label>
                <input
                  type="text"
                  value={basicUsername}
                  onChange={(e) => setBasicUsername(e.target.value)}
                  placeholder="Username"
                  autoComplete="new-password"
                />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  value={basicPassword}
                  onChange={(e) => setBasicPassword(e.target.value)}
                  placeholder="Password — stored in Key Vault"
                  autoComplete="new-password"
                />
                <p className="help-text">Sent as <code>Authorization: Basic &lt;base64&gt;</code>. Stored in Azure Key Vault.</p>
              </div>
            </>
          )}

          {authType === 'apikey' && (
            <>
              <div className="form-group">
                <label>Header Name</label>
                <input
                  type="text"
                  value={apiKeyHeaderName}
                  onChange={(e) => setApiKeyHeaderName(e.target.value)}
                  placeholder="e.g., X-Api-Key"
                />
              </div>
              <div className="form-group">
                <label>Header Value</label>
                <input
                  type="password"
                  value={apiKeyHeaderValue}
                  onChange={(e) => setApiKeyHeaderValue(e.target.value)}
                  placeholder="API key value — stored in Key Vault"
                  autoComplete="new-password"
                />
                <p className="help-text">Sent as a custom HTTP header. Stored in Azure Key Vault.</p>
              </div>
            </>
          )}

          <div className="form-group">
            <label>Configuration (JSON, optional)</label>
            <textarea
              value={configJson}
              onChange={(e) => setConfigJson(e.target.value)}
              placeholder={'{\n  "projectKey": "OPS",\n  "issueType": "Bug"\n}'}
              rows={4}
            />
          </div>

          <div className="form-actions">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary" disabled={submitting || !name.trim()}>
              {submitting ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// API Key Created Modal (shown after generating a new inbound key)
// ══════════════════════════════════════════════════════════════════════════════

const ApiKeyCreatedModal: React.FC<{
  apiKey: string;
  onClose: () => void;
}> = ({ apiKey, onClose }) => {
  const [copied, setCopied] = useState<string | null>(null);
  const webhookUrl = INGESTION_ENDPOINT;

  const handleCopy = (text: string, type: string) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <h3>API Key Created</h3>

        <div className="warning-banner">
          Copy this API key now. You won't be able to see it again!
        </div>

        <div className="form-group">
          <label>Your API Key</label>
          <div className="api-key-display">
            <code>{apiKey}</code>
            <button className="btn-icon" onClick={() => handleCopy(apiKey, 'key')} title="Copy">
              {copied === 'key' ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>

        <div className="form-group">
          <label>Webhook URL</label>
          <div className="webhook-url-display">
            <code>{webhookUrl}</code>
            <button className="btn-icon" onClick={() => handleCopy(webhookUrl, 'url')} title="Copy">
              {copied === 'url' ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>

        <div className="form-group">
          <label>Authentication Header</label>
          <div className="code-block">
            <code>{INGESTION_API_KEY_HEADER}: {apiKey}</code>
            <button className="copy-btn" onClick={() => handleCopy(apiKey, 'header')}>
              Copy Key
            </button>
          </div>
        </div>

        <div className="step">
          <h4>Quick Setup</h4>
          <ol>
            <li>In your monitoring tool, set the Webhook URL above</li>
            <li>Add a custom header: <code>{INGESTION_API_KEY_HEADER}</code> = your API key</li>
            <li>Test the connection and save</li>
          </ol>
        </div>

        <div className="form-actions">
          <button type="button" onClick={onClose} className="btn-primary">Done</button>
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// Inbound API Keys Modal
// ══════════════════════════════════════════════════════════════════════════════

export const InboundApiKeysModal: React.FC<{
  integration: InboundIntegration;
  onClose: () => void;
  onCreateKey: (reference: string) => Promise<void>;
  onDeleteKey: (keyId: string) => Promise<void>;
  onRestoreKey: (keyId: string) => Promise<void>;
  onPurgeKey: (keyId: string) => Promise<void>;
}> = ({ integration, onClose, onCreateKey, onDeleteKey, onRestoreKey, onPurgeKey }) => {
  useDateTimePreferences();
  const [keys, setKeys] = useState<ApiKeyInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmDeactivate, setConfirmDeactivate] = useState<string | null>(null);
  const [confirmPurge, setConfirmPurge] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [reference, setReference] = useState('');
  const [showMissingReferenceDialog, setShowMissingReferenceDialog] = useState(false);
  const [creatingKey, setCreatingKey] = useState(false);

  useEffect(() => {
    loadKeys();
  }, [integration.inboundIntegrationId]);

  const loadKeys = async () => {
    setLoading(true);
    try {
      const data = await tenantApi.getInboundIntegrationKeys(integration.inboundIntegrationId);
      setKeys(data);
    } catch (err) {
      console.error('Failed to load keys:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async (keyId: string) => {
    setActionLoading(keyId);
    try {
      await onDeleteKey(keyId);
      await loadKeys();
      setConfirmDeactivate(null);
    } catch (err: any) {
      alert(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRestore = async (keyId: string) => {
    setActionLoading(keyId);
    try {
      await onRestoreKey(keyId);
      await loadKeys();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handlePurge = async (keyId: string) => {
    setActionLoading(keyId);
    try {
      await onPurgeKey(keyId);
      await loadKeys();
      setConfirmPurge(null);
    } catch (err: any) {
      alert(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const createKey = async (value: string) => {
    setCreatingKey(true);
    try {
      await onCreateKey(value);
    } catch (err: any) {
      alert(err.message);
    } finally {
      setCreatingKey(false);
    }
  };

  const requestCreateKey = () => {
    const value = reference.trim();
    if (!value) {
      setShowMissingReferenceDialog(true);
      return;
    }
    void createKey(value);
  };

  const confirmGeneratedReference = () => {
    const generatedReference = `Created-${new Date().toISOString().slice(0, 10)}`;
    setReference(generatedReference);
    setShowMissingReferenceDialog(false);
    void createKey(generatedReference);
  };

  const activeKeys = keys.filter(k => k.isActive);
  const inactiveKeys = keys.filter(k => !k.isActive);
  const confirmingKey = keys.find(key => key.keyId === confirmDeactivate);
  const purgingKey = keys.find(key => key.keyId === confirmPurge && !key.isActive);

  return (
    <div className="modal-overlay api-keys-modal-overlay" onClick={onClose}>
      <div className="modal modal-lg api-keys-modal" onClick={(e) => e.stopPropagation()}>
        <h3>API Keys: {integration.name}</h3>

        <div className="keys-section">
          <div className="section-header">
            <span>Manage API keys for this inbound integration</span>
          </div>

          <div className="api-key-reference-row">
            <div className="form-group api-key-reference-field">
              <label htmlFor="inbound-api-key-reference">Reference *</label>
              <input
                id="inbound-api-key-reference"
                type="text"
                maxLength={200}
                required
                aria-required="true"
                value={reference}
                onChange={event => setReference(event.target.value)}
                placeholder="Reference/Comment"
                disabled={creatingKey}
              />
            </div>
            <button className="btn-primary api-key-generate-button" onClick={requestCreateKey} disabled={creatingKey}>
              + Generate New Key
            </button>
          </div>

          {loading ? (
            <div className="loading">Loading...</div>
          ) : keys.length === 0 ? (
            <div className="empty-state-small">
              <p>No API keys yet. Generate one to start receiving webhooks.</p>
            </div>
          ) : (
            <>
              {activeKeys.length > 0 && (
                <div className="api-key-table-wrap">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Key</th>
                        <th>Reference</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeKeys.map((key) => (
                        <tr key={key.keyId}>
                          <td><code>{key.keyPrefix}</code></td>
                          <td>{key.reference || '-'}</td>
                          <td><span className="status-badge active">Active</span></td>
                          <td>{formatDate(key.createdAt)}</td>
                          <td>
                            <button
                              className="btn-icon api-key-deactivate-button"
                              title="Deactivate"
                              onClick={() => setConfirmDeactivate(key.keyId)}
                            >
                              Deactivate
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {inactiveKeys.length > 0 && (
                <>
                  <h4 style={{ marginTop: '1rem', marginBottom: '0.5rem', color: 'var(--color-text-secondary)' }}>Deactivated Keys</h4>
                  <div className="api-key-table-wrap">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Key</th>
                          <th>Reference</th>
                          <th>Status</th>
                          <th>Purge Date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inactiveKeys.map((key) => (
                          <tr key={key.keyId} style={{ opacity: 0.7 }}>
                            <td><code>{key.keyPrefix}</code></td>
                            <td>{key.reference || '-'}</td>
                            <td><span className="status-badge inactive">Deactivated</span></td>
                            <td>{key.purgeAt ? formatDate(key.purgeAt) : '-'}</td>
                            <td>
                              <div className="api-key-inactive-actions">
                                <button
                                  className="btn-secondary btn-sm"
                                  disabled={actionLoading === key.keyId}
                                  onClick={() => handleRestore(key.keyId)}
                                >
                                  {actionLoading === key.keyId && !confirmPurge ? 'Restoring...' : 'Restore'}
                                </button>
                                <button
                                  className="btn-danger btn-sm"
                                  disabled={actionLoading === key.keyId}
                                  onClick={() => setConfirmPurge(key.keyId)}
                                >
                                  Delete permanently
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div className="form-group" style={{ marginTop: '1rem' }}>
          <label>Webhook URL</label>
          <div className="code-block">
            <code>{`POST ${INGESTION_ENDPOINT}`}</code>
          </div>
          <p className="help-text">
            Same URL for all integrations. The API key in the <strong>{INGESTION_API_KEY_HEADER}</strong> header identifies the integration and tenant.
          </p>
        </div>

        <div className="form-actions">
          <button type="button" onClick={onClose} className="btn-secondary">Close</button>
        </div>
      </div>
      {confirmingKey && (
        <div className="modal-overlay api-key-deactivate-confirm-overlay" onClick={event => {
          event.stopPropagation();
          if (event.target === event.currentTarget && actionLoading !== confirmingKey.keyId)
            setConfirmDeactivate(null);
        }}>
          <div className="modal modal-sm" role="dialog" aria-modal="true" aria-labelledby="api-key-deactivate-title"
            onClick={event => event.stopPropagation()}>
            <h3 id="api-key-deactivate-title">Deactivate API key?</h3>
            <p>Deactivate <strong>{confirmingKey.keyPrefix}</strong>?</p>
            <div className="api-key-deactivation-help">
              Requests using this key will receive HTTP 401 immediately. The key is retained for 30 days and can be restored before its purge date.
            </div>
            <div className="form-actions">
              <button type="button" className="btn-secondary" disabled={actionLoading === confirmingKey.keyId}
                onClick={() => setConfirmDeactivate(null)}>Cancel</button>
              <button type="button" className="btn-danger" disabled={actionLoading === confirmingKey.keyId}
                onClick={() => void handleDeactivate(confirmingKey.keyId)}>
                {actionLoading === confirmingKey.keyId ? 'Deactivating...' : 'Deactivate key'}
              </button>
            </div>
          </div>
        </div>
      )}
      {purgingKey && (
        <div className="modal-overlay api-key-purge-confirm-overlay" onClick={event => {
          event.stopPropagation();
          if (event.target === event.currentTarget && actionLoading !== purgingKey.keyId)
            setConfirmPurge(null);
        }}>
          <div className="modal modal-sm" role="dialog" aria-modal="true" aria-labelledby="api-key-purge-title"
            onClick={event => event.stopPropagation()}>
            <h3 id="api-key-purge-title">Permanently delete API key?</h3>
            <p>Permanently delete <strong>{purgingKey.keyPrefix}</strong>{purgingKey.reference ? <> ({purgingKey.reference})</> : null}?</p>
            <div className="api-key-deactivation-help">
              The key is removed immediately and cannot be restored. The deletion is recorded in the audit log.
            </div>
            <div className="form-actions">
              <button type="button" className="btn-secondary" disabled={actionLoading === purgingKey.keyId}
                onClick={() => setConfirmPurge(null)}>Cancel</button>
              <button type="button" className="btn-danger" disabled={actionLoading === purgingKey.keyId}
                onClick={() => void handlePurge(purgingKey.keyId)}>
                {actionLoading === purgingKey.keyId ? 'Deleting...' : 'Delete key'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showMissingReferenceDialog && (
        <div className="modal-overlay api-key-reference-confirm-overlay" onClick={event => {
          event.stopPropagation();
          if (event.target === event.currentTarget) setShowMissingReferenceDialog(false);
        }}>
          <div className="modal modal-sm" role="dialog" aria-modal="true" aria-labelledby="api-key-reference-title">
            <h3 id="api-key-reference-title">Reference not filled</h3>
            <p>The Reference field has not been filled. OK will use Created-{new Date().toISOString().slice(0, 10)}.</p>
            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={() => setShowMissingReferenceDialog(false)}>Cancel</button>
              <button type="button" className="btn-primary" onClick={confirmGeneratedReference}>OK</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegrationsManagement;

// ══════════════════════════════════════════════════════════════════════════════
// Edit Inbound Integration Modal
// ══════════════════════════════════════════════════════════════════════════════

export const EditInboundModal: React.FC<{
  integration: InboundIntegration;
  onClose: () => void;
  onSaved: () => void;
}> = ({ integration, onClose, onSaved }) => {
  const [name, setName] = useState(integration.name);
  const [provider, setProvider] = useState(integration.provider || 'Generic');
  const [nonAlert, setNonAlert] = useState(integration.nonAlert ?? false);
  const [transformationType, setTransformationType] = useState<'default' | 'jsonata' | 'mappings'>(
    (integration.transformationType as any) || 'default',
  );
  const [jsonataExpression, setJsonataExpression] = useState(
    transformationType === 'jsonata' ? (integration.transformationConfig || '') : '',
  );
  const [fieldMappings, setFieldMappings] = useState(
    transformationType === 'mappings' ? (integration.transformationConfig || '') : '',
  );
  const [isEnabled, setIsEnabled] = useState(integration.isEnabled);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [jsonError, setJsonError] = useState<string | null>(null);

  // Keep config editor in sync when transformation type changes
  const handleTypeChange = (newType: 'default' | 'jsonata' | 'mappings') => {
    setTransformationType(newType);
    setJsonError(null);
    // Pre-fill editor with existing config if switching back to the original type
    if (newType === 'jsonata') {
      setJsonataExpression(
        integration.transformationType === 'jsonata' ? (integration.transformationConfig || '') : jsonataExpression,
      );
    } else if (newType === 'mappings') {
      setFieldMappings(
        integration.transformationType === 'mappings' ? (integration.transformationConfig || '') : fieldMappings,
      );
    }
  };

  const validateAndGetConfig = (): { config: string | undefined; valid: boolean } => {
    setJsonError(null);
    if (transformationType === 'jsonata') {
      return { config: jsonataExpression.trim() || undefined, valid: true };
    }
    if (transformationType === 'mappings') {
      if (!fieldMappings.trim()) return { config: undefined, valid: true };
      try {
        JSON.parse(fieldMappings);
        return { config: fieldMappings, valid: true };
      } catch {
        setJsonError('Invalid JSON in field mappings — fix the syntax before saving.');
        return { config: undefined, valid: false };
      }
    }
    return { config: undefined, valid: true };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { config, valid } = validateAndGetConfig();
    if (!valid) return;

    setSubmitting(true);
    setError(null);
    try {
      await tenantApi.updateInboundIntegration(integration.inboundIntegrationId, {
        name,
        provider,
        nonAlert,
        transformationType,
        transformationConfig: config,
        isEnabled,
      });
      onSaved();
    } catch (err: any) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <h3>Edit Inbound Integration</h3>
        <p style={{ color: 'var(--color-text-secondary)', marginBottom: '1rem' }}>
          Update settings for <strong>{integration.name}</strong>. Changing the transformation config takes
          effect on the next inbound webhook received.
        </p>
        <form onSubmit={handleSubmit}>
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label>Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="e.g., Auvik Production Alerts"
            />
          </div>

          <div className="form-group">
            <label>Provider</label>
            <select value={provider} onChange={(e) => setProvider(e.target.value)}>
              {INBOUND_PROVIDERS.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="edit-inbound-type">Integration Type</label>
            <select id="edit-inbound-type" value={nonAlert ? 'notification' : 'alert'} onChange={(event) => setNonAlert(event.target.value === 'notification')}>
              <option value="alert">Alert</option>
              <option value="notification">Notification</option>
            </select>
          </div>

          <div className="form-group">
            <label>Legacy Payload Transformation</label>
            <select
              value={transformationType}
              onChange={(e) => handleTypeChange(e.target.value as any)}
            >
              <option value="default">Legacy default (Auvik-compatible fallback)</option>
              <option value="jsonata">JSONata (unsupported legacy value)</option>
              <option value="mappings">Legacy Field Mappings (JSON)</option>
            </select>
          </div>

          {transformationType === 'jsonata' && (
            <div className="form-group">
              <label>Saved JSONata value</label>
              <textarea
                value={jsonataExpression}
                onChange={(e) => { setJsonataExpression(e.target.value); setJsonError(null); }}
                rows={8}
                style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
              />
              <p className="help-text">
                The current ingestion runtime does not execute this saved value. It is preserved for compatibility;
                changing it will not enable JSONata. Use the Schemas action for validated registered mappings.
              </p>
            </div>
          )}

          {transformationType === 'mappings' && (
            <div className="form-group">
              <label>Legacy Field Mappings (JSON)</label>
              <textarea
                value={fieldMappings}
                onChange={(e) => { setFieldMappings(e.target.value); setJsonError(null); }}
                placeholder={'{\n  "title": "alert_name",\n  "severity": "priority"\n}'}
                rows={8}
                style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
              />
              <p className="help-text">
                Legacy source-field mappings; this is not the registered-schema JSON Pointer mapping format.
              </p>
              {jsonError && (
                <div className="error-message" style={{ marginTop: '0.5rem' }}>{jsonError}</div>
              )}
            </div>
          )}

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={isEnabled}
                onChange={(e) => setIsEnabled(e.target.checked)}
                style={{ width: 'auto', cursor: 'pointer' }}
              />
              Enabled
            </label>
          </div>

          <div className="form-actions">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary" disabled={submitting || !name.trim()}>
              {submitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// Edit Outbound Integration Modal
// ══════════════════════════════════════════════════════════════════════════════

const EditOutboundModal: React.FC<{
  integration: OutboundIntegration;
  onClose: () => void;
  onSaved: () => void;
}> = ({ integration, onClose, onSaved }) => {
  const [name, setName] = useState(integration.name);
  const [provider, setProvider] = useState(integration.provider || 'Webhook');
  const [baseUrl, setBaseUrl] = useState(integration.baseUrl || '');
  const [authType, setAuthType] = useState(integration.authType || 'none');
  // per-type credential fields (blank = keep existing)
  const [bearerToken, setBearerToken] = useState('');
  const [basicUsername, setBasicUsername] = useState('');
  const [basicPassword, setBasicPassword] = useState('');
  const [apiKeyHeaderName, setApiKeyHeaderName] = useState('');
  const [apiKeyHeaderValue, setApiKeyHeaderValue] = useState('');
  const [configJson, setConfigJson] = useState(
    integration.configJson
      ? (() => { try { return JSON.stringify(JSON.parse(integration.configJson), null, 2); } catch { return integration.configJson; } })()
      : '',
  );
  const [isEnabled, setIsEnabled] = useState(integration.isEnabled);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [jsonError, setJsonError] = useState<string | null>(null);

  const hasExistingSecret = !!integration.authSecretRef;

  const buildCredential = (): OutboundCredential | null => {
    if (authType === 'bearer' && bearerToken.trim()) {
      return { authType: 'bearer', token: bearerToken.trim() };
    }
    if (authType === 'basic' && (basicUsername.trim() || basicPassword.trim())) {
      return { authType: 'basic', username: basicUsername.trim(), password: basicPassword.trim() };
    }
    if (authType === 'apikey' && apiKeyHeaderName.trim() && apiKeyHeaderValue.trim()) {
      return { authType: 'apikey', headerName: apiKeyHeaderName.trim(), headerValue: apiKeyHeaderValue.trim() };
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setJsonError(null);

    if (configJson.trim()) {
      try {
        JSON.parse(configJson);
      } catch {
        setJsonError('Invalid JSON in configuration — fix the syntax before saving.');
        return;
      }
    }

    setSubmitting(true);
    setError(null);
    try {
      const credential = buildCredential();
      if (credential) {
        await tenantApi.storeOutboundSecret(integration.outboundIntegrationId, credential);
      }

      await tenantApi.updateOutboundIntegration(integration.outboundIntegrationId, {
        name,
        provider,
        baseUrl: baseUrl || undefined,
        authType,
        configJson: configJson.trim() || undefined,
        isEnabled,
      });
      onSaved();
    } catch (err: any) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <h3>Edit Outbound Integration</h3>
        <p style={{ color: 'var(--color-text-secondary)', marginBottom: '1rem' }}>
          Update settings for <strong>{integration.name}</strong>. Changes take effect on the next dispatch.
        </p>
        <form onSubmit={handleSubmit}>
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label>Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="e.g., Jira Production Project"
            />
          </div>

          <div className="form-group">
            <label>Provider</label>
            <select value={provider} onChange={(e) => setProvider(e.target.value)}>
              {OUTBOUND_PROVIDERS.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Base URL</label>
            <input
              type="url"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="e.g., https://your-instance.atlassian.net"
            />
          </div>

          <div className="form-group">
            <label>Authentication</label>
            <select value={authType} onChange={(e) => setAuthType(e.target.value)}>
              {AUTH_TYPES.map(a => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </select>
          </div>

          {authType === 'bearer' && (
            <div className="form-group">
              <label>{hasExistingSecret ? 'Replace Token' : 'Token'}</label>
              <input
                type="password"
                value={bearerToken}
                onChange={(e) => setBearerToken(e.target.value)}
                placeholder={hasExistingSecret ? 'Leave blank to keep existing token' : 'Bearer token — stored in Key Vault'}
                autoComplete="new-password"
              />
              <p className="help-text">
                {hasExistingSecret
                  ? 'A token is already stored. Enter a new value to rotate it, or leave blank to keep the existing one.'
                  : 'Sent as Authorization: Bearer <token>. Stored in Azure Key Vault.'}
              </p>
            </div>
          )}

          {authType === 'basic' && (
            <>
              <div className="form-group">
                <label>{hasExistingSecret ? 'Replace Username' : 'Username'}</label>
                <input
                  type="text"
                  value={basicUsername}
                  onChange={(e) => setBasicUsername(e.target.value)}
                  placeholder={hasExistingSecret ? 'Leave blank to keep existing credentials' : 'Username'}
                  autoComplete="new-password"
                />
              </div>
              <div className="form-group">
                <label>{hasExistingSecret ? 'Replace Password' : 'Password'}</label>
                <input
                  type="password"
                  value={basicPassword}
                  onChange={(e) => setBasicPassword(e.target.value)}
                  placeholder={hasExistingSecret ? 'Leave blank to keep existing credentials' : 'Password — stored in Key Vault'}
                  autoComplete="new-password"
                />
                <p className="help-text">
                  {hasExistingSecret
                    ? 'Credentials already stored. Fill both fields to rotate, or leave blank to keep existing.'
                    : 'Sent as Authorization: Basic <base64>. Stored in Azure Key Vault.'}
                </p>
              </div>
            </>
          )}

          {authType === 'apikey' && (
            <>
              <div className="form-group">
                <label>{hasExistingSecret ? 'Replace Header Name' : 'Header Name'}</label>
                <input
                  type="text"
                  value={apiKeyHeaderName}
                  onChange={(e) => setApiKeyHeaderName(e.target.value)}
                  placeholder={hasExistingSecret ? 'Leave blank to keep existing key' : 'e.g., X-Api-Key'}
                />
              </div>
              <div className="form-group">
                <label>{hasExistingSecret ? 'Replace Header Value' : 'Header Value'}</label>
                <input
                  type="password"
                  value={apiKeyHeaderValue}
                  onChange={(e) => setApiKeyHeaderValue(e.target.value)}
                  placeholder={hasExistingSecret ? 'Leave blank to keep existing key' : 'API key value — stored in Key Vault'}
                  autoComplete="new-password"
                />
                <p className="help-text">
                  {hasExistingSecret
                    ? 'An API key is already stored. Fill both fields to rotate, or leave blank to keep existing.'
                    : 'Sent as a custom HTTP header. Stored in Azure Key Vault.'}
                </p>
              </div>
            </>
          )}

          <div className="form-group">
            <label>Configuration (JSON, optional)</label>
            <textarea
              value={configJson}
              onChange={(e) => { setConfigJson(e.target.value); setJsonError(null); }}
              placeholder={'{\n  "projectKey": "OPS",\n  "issueType": "Bug"\n}'}
              rows={8}
              style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
            />
            <p className="help-text">
              Provider-specific settings such as project keys, custom header names, or request body
              templates. Must be valid JSON.
            </p>
            {jsonError && (
              <div className="error-message" style={{ marginTop: '0.5rem' }}>{jsonError}</div>
            )}
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={isEnabled}
                onChange={(e) => setIsEnabled(e.target.checked)}
                style={{ width: 'auto', cursor: 'pointer' }}
              />
              Enabled
            </label>
          </div>

          <div className="form-actions">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary" disabled={submitting || !name.trim()}>
              {submitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const OutboundActionsModal: React.FC<{
  integration: OutboundIntegration;
  inboundIntegrations: InboundIntegration[];
  onClose: () => void;
}> = ({ integration, inboundIntegrations, onClose }) => {
  type ActionConfirmState =
    | { mode: 'discard-open'; action: OutboundIntegrationAction }
    | { mode: 'discard-new' }
    | { mode: 'delete-action'; action: OutboundIntegrationAction };

  useDateTimePreferences();
  const [actions, setActions] = useState<OutboundIntegrationAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingActionId, setEditingActionId] = useState<string | null>(null);
  const [samplePayloadInput, setSamplePayloadInput] = useState<string>('');
  const [samplePayloadError, setSamplePayloadError] = useState<string | null>(null);
  const [samplePayloadTokens, setSamplePayloadTokens] = useState<string[]>([]);
  const [requestSchemaError, setRequestSchemaError] = useState<string | null>(null);
  const [responseSchemaError, setResponseSchemaError] = useState<string | null>(null);
  const [requestFieldRefreshKey, setRequestFieldRefreshKey] = useState(0);
  const [requestFieldRefreshStatus, setRequestFieldRefreshStatus] = useState<string | null>(null);
  const [responseFieldRefreshKey, setResponseFieldRefreshKey] = useState(0);
  const [responseFieldRefreshStatus, setResponseFieldRefreshStatus] = useState<string | null>(null);
  const [sampleResponseJson, setSampleResponseJson] = useState('');
  const [sampleResponseError, setSampleResponseError] = useState<string | null>(null);
  const [activeNormalizedPayloadTokens, setActiveNormalizedPayloadTokens] = useState<string[]>([]);
  const [activeNormalizedPayloadVariables, setActiveNormalizedPayloadVariables] = useState<OutboundPayloadVariable[]>([]);
  const [activeNormalizedPayloadStatus, setActiveNormalizedPayloadStatus] = useState('Loading active inbound workflow payload schemas...');
  const [templateTokenCategory, setTemplateTokenCategory] = useState<'All' | 'Common' | 'Payload' | 'Steps'>('All');
  const [templateTarget, setTemplateTarget] = useState<'pathTemplate' | 'bodyTemplate' | 'header'>('bodyTemplate');
  const [templateHeaderName, setTemplateHeaderName] = useState('');
  const [testVariablesInput, setTestVariablesInput] = useState<string>('{}');
  const [mappingEditorMode, setMappingEditorMode] = useState<'visual' | 'expert'>('visual');
  const [testRunning, setTestRunning] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<any | null>(null);
  const [requestSectionOpen, setRequestSectionOpen] = useState(true);
  const [responseSectionOpen, setResponseSectionOpen] = useState(false);
  const [postProcessingSectionOpen, setPostProcessingSectionOpen] = useState(false);
  const [lastSaveSummary, setLastSaveSummary] = useState<{
    mode: 'created' | 'updated';
    actionName: string;
    actionKey: string;
    savedAt: string;
  } | null>(null);
  const [actionDraftOpen, setActionDraftOpen] = useState(false);
  const [pendingActionConfirm, setPendingActionConfirm] = useState<ActionConfirmState | null>(null);
  const [actionConfirmSubmitting, setActionConfirmSubmitting] = useState(false);
  const bodyTemplateRef = useRef<HTMLTextAreaElement | null>(null);
  const [form, setForm] = useState<CreateOutboundIntegrationActionRequest>(createDefaultOutboundActionForm());
  const [persistedForm, setPersistedForm] = useState<CreateOutboundIntegrationActionRequest>(createDefaultOutboundActionForm());

  const draftIsDirty = useMemo(
    () => buildActionDraftSnapshot(form) !== buildActionDraftSnapshot(persistedForm),
    [form, persistedForm],
  );

  const actionTokens = useMemo(
    () => extractActionTokens(form.pathTemplate, form.bodyTemplate, ...Object.values(form.headers || {})),
    [form.pathTemplate, form.bodyTemplate, form.headers],
  );

  const integrationFieldTokens = useMemo(
    () => actionTokens.filter((token) => token.startsWith('integration.fields.')),
    [actionTokens],
  );

  const requiresAssociatedTicketId = form.actionKey === 'updateTicket' || form.actionKey === 'closeTicket';
  const usesAssociatedTicketIdToken = actionTokens.includes('integration.ticketId');

  const providerPresetsGuide = React.useMemo(() => {
    const provider = (integration.provider || '').toLowerCase();
    if (provider === 'jira') return 'Jira presets are auto-seeded (Create/Update/Close issue).';
    if (provider === 'servicenow') return 'ServiceNow presets are auto-seeded (Create/Update/Resolve incident).';
    if (provider === 'pagerduty') return 'PagerDuty presets are auto-seeded (Create/Update/Resolve incident).';
    return 'Generic presets are auto-seeded (Create/Update/Close ticket).';
  }, [integration.provider]);

  useEffect(() => {
    let cancelled = false;
    const enabledIntegrations = inboundIntegrations.filter(item => item.isEnabled);
    if (!enabledIntegrations.length) {
      setActiveNormalizedPayloadTokens([]);
      setActiveNormalizedPayloadVariables([]);
      setActiveNormalizedPayloadStatus('No enabled inbound integrations are available as payload sources.');
      return () => { cancelled = true; };
    }

    setActiveNormalizedPayloadStatus('Loading active inbound workflow payload schemas...');
    Promise.all(enabledIntegrations.map(async item => {
      try {
        const registry = await inboundSchemasApi.list(item.inboundIntegrationId);
        const schema = effectiveOutputSchema(registry.settings);
        if (!schema) return { tokens: [] as string[], qualified: [] as OutboundPayloadVariable[] };
        const tokens = deriveActiveNormalizedPayloadTokens(item, schema);
        const qualified = deriveQualifiedNormalizedPayloadVariables(item, schema);
        return { tokens, qualified };
      } catch {
        return { tokens: [] as string[], qualified: [] as OutboundPayloadVariable[] };
      }
    })).then(results => {
      if (cancelled) return;
      const tokens = Array.from(new Set(results.flatMap(result => result.tokens))).slice(0, 200);
      const qualified = Array.from(new Map(results.flatMap(result => result.qualified).map(variable => [variable.path, variable])).values()).slice(0, 200);
      const schemaCount = results.filter(result => result.qualified.length > 0).length;
      setActiveNormalizedPayloadTokens(tokens);
      setActiveNormalizedPayloadVariables(qualified);
      setActiveNormalizedPayloadStatus(qualified.length
        ? `Loaded ${qualified.length} source-qualified fields from ${schemaCount} active inbound workflow payload schemas (Direct input or Normalise output). Use the integration-labelled path when field names overlap; draft-only schemas are excluded.`
        : 'No active workflow payload schemas are available. Draft-only schemas are not workflow variables.');
    });

    return () => { cancelled = true; };
  }, [inboundIntegrations]);

  const loadActions = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await tenantApi.getOutboundIntegrationActions(integration.outboundIntegrationId);
      const loadedActions = Array.isArray(result) ? result : [];
      setActions(loadedActions);
      if (!actionDraftOpen && !editingActionId && loadedActions.length > 0) {
        startEdit(loadedActions[0]);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load actions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadActions();
  }, [integration.outboundIntegrationId]);

  const resetForm = () => {
    setEditingActionId(null);
    setActionDraftOpen(false);
    const emptyForm = createDefaultOutboundActionForm();
    setForm(emptyForm);
    setPersistedForm(emptyForm);
    setLastSaveSummary(null);
    setTestError(null);
    setTestResult(null);
    setRequestSchemaError(null);
    setResponseSchemaError(null);
    setRequestFieldRefreshStatus(null);
    setResponseFieldRefreshStatus(null);
  };

  const startEdit = (action: OutboundIntegrationAction) => {
    const actionForm = prepareActionFormForEdit(action, integration.provider || 'Webhook');
    setEditingActionId(action.outboundIntegrationActionId);
    setActionDraftOpen(true);
    setForm(actionForm);
    setPersistedForm(toActionForm(action));
    setLastSaveSummary(null);
    setTestError(null);
    setTestResult(null);
    setRequestSchemaError(null);
    setResponseSchemaError(null);
    setRequestFieldRefreshStatus(null);
    setResponseFieldRefreshStatus(null);
  };

  const startNewAction = () => {
    const emptyForm = createDefaultOutboundActionForm();
    setEditingActionId(null);
    setActionDraftOpen(true);
    setForm(emptyForm);
    setPersistedForm(emptyForm);
    setLastSaveSummary(null);
    setTestError(null);
    setTestResult(null);
    setRequestSchemaError(null);
    setResponseSchemaError(null);
    setRequestFieldRefreshStatus(null);
    setResponseFieldRefreshStatus(null);
  };

  const selectAction = (action: OutboundIntegrationAction) => {
    if (actionDraftOpen && draftIsDirty
      ) {
      setPendingActionConfirm({ mode: 'discard-open', action });
      return;
    }
    startEdit(action);
  };

  const createActionDraft = () => {
    if (actionDraftOpen && draftIsDirty
      ) {
      setPendingActionConfirm({ mode: 'discard-new' });
      return;
    }
    startNewAction();
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const normalizedPayload: CreateOutboundIntegrationActionRequest = {
        ...form,
        pathTemplate: normalizeLegacyTokenSyntax(form.pathTemplate || ''),
        bodyTemplate: normalizeLegacyTokenSyntax(form.bodyTemplate || ''),
        requestSchemaJson: form.requestSchemaJson?.trim() || '',
        headers: Object.fromEntries(
          Object.entries(form.headers || {}).map(([k, v]) => [k, normalizeLegacyTokenSyntax(v || '')]),
        ),
      };
      const requestMappingError = getRequestMappingValidation(normalizedPayload.requestMappingsJson).error;
      if (requestMappingError) {
        setError(requestMappingError);
        return;
      }
      normalizedPayload.requestMappingsJson = serializeOutboundRequestMappingDocument(
        parseOutboundRequestMappingDocument(normalizedPayload.requestMappingsJson || emptyRequestMappingsJson()),
      );
      const responseMappingError = getResponseMappingValidation(normalizedPayload.responseMappingsJson).error;
      if (responseMappingError) {
        setError(responseMappingError);
        return;
      }
      if (normalizedPayload.requestSchemaJson) {
        const schema = JSON.parse(normalizedPayload.requestSchemaJson);
        if (!schema || typeof schema !== 'object' || Array.isArray(schema)) {
          setRequestSchemaError('Outbound request schema must be a JSON object.');
          return;
        }
        normalizedPayload.requestSchemaJson = stripOutboundRequestSchemaRequired(normalizedPayload.requestSchemaJson);
      }

      const savedAction = editingActionId
        ? await tenantApi.updateOutboundIntegrationAction(integration.outboundIntegrationId, editingActionId, normalizedPayload)
        : await tenantApi.createOutboundIntegrationAction(integration.outboundIntegrationId, normalizedPayload);

      const savedForm = toActionForm(savedAction);
      setEditingActionId(savedAction.outboundIntegrationActionId);
      setActionDraftOpen(true);
      setForm(savedForm);
      setPersistedForm(savedForm);
      setLastSaveSummary({
        mode: editingActionId ? 'updated' : 'created',
        actionName: savedAction.name,
        actionKey: savedAction.actionKey,
        savedAt: new Date().toISOString(),
      });

      await loadActions();
    } catch (err: any) {
      setError(err.message || 'Failed to save action');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSamplePayloadParse = () => {
    setSamplePayloadError(null);
    const raw = samplePayloadInput.trim();
    if (!raw) {
      setSamplePayloadTokens([]);
      return;
    }

    try {
      const parsed = JSON.parse(raw);
          const tokens = extractNormalizedPayloadTokens(parsed).sort();
      setSamplePayloadTokens(tokens);
    } catch {
      setSamplePayloadError('Sample payload must be valid JSON before extracting token suggestions.');
      setSamplePayloadTokens([]);
    }
  };

  const refreshRequestFields = () => {
    try {
      const fields = form.requestSchemaJson?.trim() ? outboundSchemaFields(form.requestSchemaJson) : [];
      setRequestSchemaError(null);
      setRequestFieldRefreshKey(value => value + 1);
      setRequestFieldRefreshStatus(fields.length
        ? `Refreshed ${fields.length} request fields. Existing mapping rules were preserved.`
        : 'No selectable fields found. Add JSON Schema properties, then refresh fields.');
    } catch {
      setRequestSchemaError('Outbound request schema must be valid JSON before fields can be refreshed.');
      setRequestFieldRefreshStatus(null);
    }
  };

  const refreshResponseFields = () => {
    try {
      const schemaValue = form.responseSchemaJson?.trim() || '{}';
      const parsed = JSON.parse(schemaValue) as { properties?: unknown; $schema?: unknown; $ref?: unknown };
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        && Object.keys(parsed).length > 0 && !parsed.properties && !parsed.$schema && !parsed.$ref) {
        setResponseSchemaError('This looks like a sample response payload, not JSON Schema. Generate a schema from the sample response below.');
        setResponseFieldRefreshStatus(null);
        return;
      }
      const fields = outboundSchemaFields(schemaValue);
      setResponseSchemaError(null);
      setResponseFieldRefreshKey(value => value + 1);
      setResponseFieldRefreshStatus(fields.length
        ? `Refreshed ${fields.length} response fields.`
        : 'No selectable response fields found. Add JSON Schema properties, then refresh fields.');
    } catch {
      setResponseSchemaError('Outbound response schema must be valid JSON before fields can be refreshed.');
      setResponseFieldRefreshStatus(null);
    }
  };

  const generateResponseSchemaFromSample = () => {
    try {
      const sample = sampleResponseJson.trim();
      if (!sample) throw new Error('Sample response JSON is required.');
      JSON.parse(sample);
      const schema = formatJsonDocument(inferSchemaFromResponseSample(sample));
      const fields = outboundSchemaFields(schema);
      setForm(previous => ({ ...previous, responseSchemaJson: schema }));
      setResponseSchemaError(null);
      setResponseFieldRefreshKey(value => value + 1);
      setResponseFieldRefreshStatus(`Generated schema from sample response and refreshed ${fields.length} response fields. Existing mappings were preserved.`);
      setSampleResponseError(null);
    } catch {
      setSampleResponseError('Sample response must be valid JSON before a schema can be generated.');
      setResponseFieldRefreshStatus(null);
    }
  };

  const generateRequestSchemaFromBody = () => {
    try {
      JSON.parse(form.bodyTemplate || '{}');
      const schema = inferSchemaFromBodyTemplate(form.bodyTemplate || '{}');
      const fields = outboundSchemaFields(schema);
      setForm(previous => ({ ...previous, requestSchemaJson: schema }));
      setRequestSchemaError(null);
      setRequestFieldRefreshKey(value => value + 1);
      setRequestFieldRefreshStatus(`Generated schema from Body Template and refreshed ${fields.length} request fields. Existing mapping rules were preserved.`);
    } catch {
      setRequestSchemaError('Body Template must be valid JSON before a request schema can be generated.');
      setRequestFieldRefreshStatus(null);
    }
  };

  const appendToBodyTemplate = (token: string) => {
    const current = form.bodyTemplate || '';
    const textarea = bodyTemplateRef.current;
    if (!textarea) {
      const separator = current.length > 0 && !current.endsWith('\n') ? '\n' : '';
      setForm({ ...form, bodyTemplate: `${current}${separator}${token}` });
      return;
    }

    const start = textarea.selectionStart ?? current.length;
    const end = textarea.selectionEnd ?? start;
    const next = `${current.slice(0, start)}${token}${current.slice(end)}`;
    const caret = start + token.length;

    setForm({ ...form, bodyTemplate: next });

    window.requestAnimationFrame(() => {
      const target = bodyTemplateRef.current;
      if (!target) return;
      target.focus();
      target.setSelectionRange(caret, caret);
    });
  };

  const previewTokenValues = React.useMemo(() => {
    const values = { ...ACTION_SAMPLE_TOKEN_VALUES };
    samplePayloadTokens.forEach((token, idx) => {
      const key = token.replace(/^\{\{\s*|\s*\}\}$/g, '');
      if (values[key] == null || values[key] === '') {
        values[key] = `sample-value-${idx + 1}`;
      }
    });
    return values;
  }, [samplePayloadTokens]);

  const bodyTemplatePreview = React.useMemo(
    () => buildMappedRequestPreview(
      form.bodyTemplate || '',
      form.requestSchemaJson || '',
      form.requestMappingsJson || '{}',
      previewTokenValues,
    ),
    [form.bodyTemplate, form.requestSchemaJson, form.requestMappingsJson, previewTokenValues],
  );

  const integrationVariableHints = useMemo(() => {
    const requestMappings = parseRequestFieldMappings(form.requestMappingsJson);
    const responseMappings = parseStringMap(form.responseMappingsJson);
    const values = new Set<string>();

    Object.values(requestMappings).flatMap(mapping => 'source' in mapping ? [mapping.source] : 'sources' in mapping ? mapping.sources : [])
      .filter(path => path.startsWith('integration.'))
      .forEach(path => values.add(path));

    Object.keys(responseMappings)
      .filter(path => path.startsWith('integration.'))
      .forEach(path => values.add(path));

    if ((form.ticketIdVariable || '').trim()) {
      values.add('integration.ticketId');
    }

    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [form.requestMappingsJson, form.responseMappingsJson, form.ticketIdVariable]);

  const editingAction = useMemo(
    () => actions.find(action => action.outboundIntegrationActionId === editingActionId) || null,
    [actions, editingActionId],
  );
  const providerPresetDefaults = useMemo(
    () => buildProviderPresetDefaults(integration.provider || 'Webhook', form.actionKey),
    [integration.provider, form.actionKey],
  );
  const canResetToProviderDefaults = Boolean(editingAction?.isSystemPreset && providerPresetDefaults);

  const requestMappingValidation = useMemo(
    () => getRequestMappingValidation(form.requestMappingsJson),
    [form.requestMappingsJson],
  );
  const responseMappingValidation = useMemo(
    () => getResponseMappingValidation(form.responseMappingsJson),
    [form.responseMappingsJson],
  );
  const mappingRuleCount = requestMappingValidation.count + responseMappingValidation.count;

  const visibleActionTokens = ACTION_TEMPLATE_TOKENS.filter(token =>
    templateTokenCategory === 'All' || token.category === templateTokenCategory);
  const visibleSamplePayloadTokens = templateTokenCategory === 'All' || templateTokenCategory === 'Payload'
    ? samplePayloadTokens : [];

  const appendActionToken = (token: string) => {
    if (templateTarget === 'bodyTemplate') {
      appendToBodyTemplate(token);
      return;
    }
    if (templateTarget === 'pathTemplate') {
      setForm(previous => ({ ...previous, pathTemplate: `${previous.pathTemplate || ''}${token}` }));
      return;
    }
    const headerName = templateHeaderName.trim();
    if (!headerName || /authorization|api[-_]?key|token|secret|credential/i.test(headerName)) return;
    setForm(previous => ({
      ...previous,
      headers: {
        ...(previous.headers || {}),
        [headerName]: `${previous.headers?.[headerName] || ''}${previous.headers?.[headerName] ? ' ' : ''}${token}`,
      },
    }));
  };

  const remove = async (action: OutboundIntegrationAction) => {
    setPendingActionConfirm({ mode: 'delete-action', action });
  };

  const resolveActionConfirmCopy = (state: ActionConfirmState) => {
    if (state.mode === 'discard-open') {
      return {
        title: 'Discard unsaved changes?',
        message: 'Discard unsaved action changes and open this action?',
        confirmLabel: 'Discard and Open',
        confirmClassName: 'btn-primary',
      };
    }
    if (state.mode === 'discard-new') {
      return {
        title: 'Discard unsaved changes?',
        message: 'Discard unsaved action changes and create a new action?',
        confirmLabel: 'Discard and Create',
        confirmClassName: 'btn-primary',
      };
    }
    const source = state.action.isSystemPreset ? 'provider preset' : 'custom action';
    return {
      title: 'Delete action',
      message: `Delete ${source} '${state.action.name}'? It will be removed from this integration and workflows using it may need updating.`,
      confirmLabel: 'Delete',
      confirmClassName: 'btn-danger',
    };
  };

  const confirmActionPrompt = async () => {
    if (!pendingActionConfirm) return;

    if (pendingActionConfirm.mode === 'discard-open') {
      const action = pendingActionConfirm.action;
      setPendingActionConfirm(null);
      startEdit(action);
      return;
    }

    if (pendingActionConfirm.mode === 'discard-new') {
      setPendingActionConfirm(null);
      startNewAction();
      return;
    }

    const { action } = pendingActionConfirm;
    setActionConfirmSubmitting(true);
    try {
      await tenantApi.deleteOutboundIntegrationAction(integration.outboundIntegrationId, action.outboundIntegrationActionId);
      if (editingActionId === action.outboundIntegrationActionId) resetForm();
      await loadActions();
      setPendingActionConfirm(null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete action');
      setPendingActionConfirm(null);
    } finally {
      setActionConfirmSubmitting(false);
    }
  };

  const closeActionPrompt = () => {
    if (actionConfirmSubmitting) return;
    setPendingActionConfirm(null);
  };

  const runLiveTest = async () => {
    const actionId = editingActionId;
    if (!actionId) {
      setTestError('Save the action first before running a live test.');
      return;
    }

    setTestRunning(true);
    setTestError(null);
    setTestResult(null);

    try {
      let parsedVariables: Record<string, string> = {};
      const raw = testVariablesInput.trim();
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          parsedVariables = Object.fromEntries(
            Object.entries(parsed as Record<string, unknown>).map(([k, v]) => [k, v == null ? '' : String(v)]),
          );
        } else {
          throw new Error('Variables must be a JSON object map.');
        }
      }

      const result = await tenantApi.testOutboundIntegrationAction(
        integration.outboundIntegrationId,
        actionId,
        { variables: parsedVariables },
      );
      setTestResult(result);
    } catch (err: any) {
      setTestError(err.message || 'Live test failed');
    } finally {
      setTestRunning(false);
    }
  };

  return (
    <div className="modal-overlay outbound-action-cabinet-overlay" onClick={onClose}>
      <div className="modal modal-lg modal-scroll outbound-action-cabinet-modal" onClick={(e) => e.stopPropagation()}>
        <header className="outbound-action-cabinet-header">
          <div>
            <h3>Webhook Actions: {integration.name}</h3>
            <p>Reusable requests for workflows. Manage each action here.</p>
            <span className="help-text">{providerPresetsGuide}</span>
          </div>
          <button type="button" className="btn-icon" onClick={onClose} aria-label="Close Integration Actions" title="Close">
            <X size={18} />
          </button>
        </header>
        <div className="outbound-action-cabinet-help"><IntegrationActionsHowItWorks /></div>
        {error && <div className="error-message">{error}</div>}
        <div className="outbound-action-cabinet">
          <aside className="outbound-action-cabinet-rail" aria-label="Webhook action cabinet">
            <nav aria-label="Webhook action views">
              <button type="button" aria-current="page"><FileJson size={16} />Actions</button>
            </nav>
            <div className="outbound-action-cabinet-tools">
              <button type="button" className="btn-secondary btn-sm" onClick={createActionDraft}>
                <Plus size={16} />Add Action
              </button>
              <button type="button" className="btn-icon" onClick={() => void loadActions()} aria-label="Reload actions" title="Reload actions">
                <RefreshCw size={16} />
              </button>
            </div>
            {loading ? <p className="help-text" role="status">Loading actions...</p> : (
              <ul className="outbound-action-cabinet-list" aria-label="Registered actions">
                {actions.map(action => {
                  const tokens = extractActionTokens(action.pathTemplate, action.bodyTemplate, ...Object.values(action.headers || {}));
                  const providerParity = getProviderParityHints(integration.provider || '', action, tokens);
                  const integrationFields = tokens.filter(token => token.startsWith('integration.fields.'));
                  return (
                    <li key={action.outboundIntegrationActionId}>
                      <button type="button" className="outbound-action-cabinet-entry"
                        aria-pressed={editingActionId === action.outboundIntegrationActionId && actionDraftOpen}
                        onClick={() => selectAction(action)}>
                        <strong>{action.name}</strong>
                        <span>{action.actionKey} · {action.method} · {action.isEnabled ? 'Enabled' : 'Disabled'}</span>
                        <code>{action.pathTemplate || '/'}</code>
                        <small>{tokens.length} tokens · {integrationFields.length} integration fields · {providerParity.status === 'good' ? 'Ready' : 'Review'}</small>
                      </button>
                      <button type="button" className="btn-icon outbound-action-cabinet-delete"
                        aria-label={`Delete ${action.name}`} title={`Delete ${action.name}`} onClick={() => void remove(action)}>
                        <Trash2 size={15} />
                      </button>
                    </li>
                  );
                })}
                {!actions.length && <li className="help-text">No actions yet.</li>}
              </ul>
            )}
          </aside>
          <main className="outbound-action-cabinet-main">
            {loading ? <div className="loading">Loading actions...</div> : !actionDraftOpen ? (
              <div className="outbound-action-cabinet-empty">
                <h4>Actions</h4>
                <p>Select an action or add one to edit its request and response mappings.</p>
              </div>
            ) : <form onSubmit={submit}>
          <div className="outbound-action-cabinet-form-scroll">
          <h4 style={{ marginTop: 0 }}>Action Draft</h4>
          <p className="help-text" style={{ marginTop: 0, marginBottom: '0.75rem' }}>
            {editingActionId ? 'Editing selected action.' : 'Create a new action.'}
          </p>
          {canResetToProviderDefaults && (
            <div className="guide-callout" style={{ marginBottom: 10 }}>
              <strong>Provider Defaults</strong>
              <div className="help-text" style={{ marginTop: 6 }}>
                Restore provider default method/path/body/schema/mappings for this preset action.
              </div>
              <div style={{ marginTop: 8 }}>
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() => {
                    if (!providerPresetDefaults) return;
                    setForm(previous => ({ ...previous, ...providerPresetDefaults }));
                    setError(null);
                  }}
                >
                  Reset To Provider Defaults
                </button>
              </div>
            </div>
          )}

          <div className="guide-callout" style={{ marginBottom: 10 }}>
            <strong>Mapping Persistence</strong>
            <div className="help-text" style={{ marginTop: 6 }}>
              Draft Status:{' '}
              <strong>{draftIsDirty ? 'Unsaved changes' : 'All changes saved'}</strong>
            </div>
            <div className="help-text" style={{ marginTop: 6 }}>
              Save persists this action; no outbound request is sent until a workflow executes it.
            </div>
            {lastSaveSummary && (
              <div className="help-text" style={{ marginTop: 6 }}>
                Last save: <strong>{lastSaveSummary.mode === 'created' ? 'Created' : 'Updated'}</strong> <code>{lastSaveSummary.actionName}</code> ({lastSaveSummary.actionKey}) at{' '}
                <strong>{formatDateTime(lastSaveSummary.savedAt)}</strong>
              </div>
            )}
            {draftIsDirty && (
              <div style={{ marginTop: 8 }}>
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() => {
                    setForm(persistedForm);
                    setError(null);
                  }}
                >
                  Revert Draft
                </button>
              </div>
            )}
          </div>

          <section aria-label="Delivery capability" style={{ marginBottom: 16 }}>
            <label>
              <input type="checkbox" checked={form.isDeliveryCapable === true}
                onChange={event => setForm(previous => ({ ...previous, isDeliveryCapable: event.target.checked }))} />
              {' '}Delivery-capable action
            </label>
            {form.isDeliveryCapable && <>
              {(form.deliveryInputs || []).map((field, index) => (
                <div className="form-row" key={index} style={{ flexWrap: 'wrap', alignItems: 'end', marginTop: 8 }}>
                  <label className="form-group" style={{ flex: '1 1 140px', minWidth: 0 }}>
                    Field name
                    <input aria-label={`Delivery field ${index + 1} name`} value={field.name} required
                      onChange={event => setForm(previous => ({ ...previous, deliveryInputs: (previous.deliveryInputs || []).map((item, itemIndex) => itemIndex === index ? { ...item, name: event.target.value } : item) }))} />
                  </label>
                  <label className="form-group" style={{ flex: '1 1 180px', minWidth: 0 }}>
                    Request JSON Pointer
                    <input aria-label={`Delivery field ${index + 1} path`} value={field.path} required placeholder="/ticketNumber"
                      onChange={event => setForm(previous => ({ ...previous, deliveryInputs: (previous.deliveryInputs || []).map((item, itemIndex) => itemIndex === index ? { ...item, path: event.target.value } : item) }))} />
                  </label>
                  <label><input type="checkbox" aria-label={`Delivery field ${index + 1} optional`} checked={!field.required}
                    onChange={event => setForm(previous => ({ ...previous, deliveryInputs: (previous.deliveryInputs || []).map((item, itemIndex) => itemIndex === index ? { ...item, required: !event.target.checked } : item) }))} /> Optional</label>
                  <button type="button" className="btn-secondary btn-sm" title="Remove delivery field" aria-label={`Remove delivery field ${index + 1}`}
                    onClick={() => setForm(previous => ({ ...previous, deliveryInputs: (previous.deliveryInputs || []).filter((_, itemIndex) => itemIndex !== index) }))}><Trash2 size={16} /></button>
                </div>
              ))}
              <button type="button" className="btn-secondary btn-sm" style={{ marginTop: 8 }}
                onClick={() => setForm(previous => ({ ...previous, deliveryInputs: [...(previous.deliveryInputs || []), { name: '', path: '', required: true }] }))}>
                <Plus size={16} aria-hidden="true" /> Add delivery field
              </button>
            </>}
          </section>

          <section className="outbound-action-section-card" aria-labelledby="outbound-request-heading">
            <div className="outbound-action-section-header">
              <h5 id="outbound-request-heading">Request</h5>
              <button type="button" className="outbound-action-section-toggle"
                aria-expanded={requestSectionOpen} aria-controls="outbound-request-content"
                onClick={() => setRequestSectionOpen(open => !open)}>
                {requestSectionOpen ? 'Hide Request' : 'Show Request'}
                <ChevronDown size={16} aria-hidden="true" />
              </button>
            </div>
            {requestSectionOpen && <div id="outbound-request-content" className="outbound-action-section-body">
          <div className="form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label>Name *</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Action Key *</label>
              <select value={form.actionKey} onChange={e => setForm({ ...form, actionKey: e.target.value })}>
                <option value="createTicket">createTicket</option>
                <option value="updateTicket">updateTicket</option>
                <option value="closeTicket">closeTicket</option>
                <option value="custom">custom</option>
              </select>
            </div>
            <div className="form-group" style={{ flex: 0.6 }}>
              <label>Method *</label>
              <select value={form.method} onChange={e => setForm({ ...form, method: e.target.value })}>
                <option>GET</option><option>POST</option><option>PUT</option><option>PATCH</option><option>DELETE</option><option>HEAD</option><option>OPTIONS</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Path Template</label>
            <input value={form.pathTemplate || ''} onChange={e => setForm({ ...form, pathTemplate: e.target.value })} placeholder="/api/v1/tickets/{{integration.ticketId}}" />
          </div>
          <div className="form-group">
            <label>Headers</label>
            <SimpleKeyValueEditor value={form.headers || {}} onChange={(headers) => setForm({ ...form, headers })} />
          </div>
          <div className="form-group">
            <label>Body Template (JSON)</label>
            <textarea
              ref={bodyTemplateRef}
              value={form.bodyTemplate || ''}
              onChange={e => {
                setForm({ ...form, bodyTemplate: e.target.value });
                setRequestFieldRefreshStatus(null);
              }}
              rows={9}
            />
            <p className="help-text" style={{ marginTop: 6 }}>
              Use tokens like <code>{'{{alert.description}}'}</code>, <code>{'{{alert.data.details}}'}</code>, and <code>{'{{integration.ticketId}}'}</code>. Legacy <code>{'{alert.description}'}</code> will auto-normalize on save.
            </p>
          </div>
          <div className="outbound-action-schema-generation">
            <button type="button" className="btn-secondary btn-sm" onClick={generateRequestSchemaFromBody} aria-label="Generate schema from Body Template">
              <FileJson size={15} />Generate schema from Body Template
            </button>
          </div>
          <div className="form-group">
            <label>Outbound Request Schema (JSON Schema)</label>
            <textarea
              value={form.requestSchemaJson || ''}
              onChange={event => {
                const value = event.target.value;
                setRequestFieldRefreshStatus(null);
                let requestSchemaJson = value;
                if (!value.trim()) setRequestSchemaError(null);
                else {
                  try {
                    const parsed = JSON.parse(value);
                    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                      setRequestSchemaError(null);
                      requestSchemaJson = stripOutboundRequestSchemaRequired(value);
                    } else setRequestSchemaError('Outbound request schema must be a JSON object.');
                  } catch {
                    setRequestSchemaError('Outbound request schema must be valid JSON.');
                  }
                }
                setForm(previous => ({ ...previous, requestSchemaJson }));
              }}
              rows={8}
              placeholder={'{\n  "type": "object",\n  "properties": {\n    "title": { "type": "string" }\n  }\n}'}
            />
            <p className="help-text" style={{ marginTop: 6 }}>Saved with this action. Workflows use its declared paths as outbound mapping targets. Required targets are stored with the mapping rules, not in this schema.</p>
            {requestSchemaError && <p className="error-message" role="alert">{requestSchemaError}</p>}
            <div className="outbound-action-schema-refresh">
              <button type="button" className="btn-secondary btn-sm" onClick={refreshRequestFields} aria-label="Refresh request mapping fields">
                <RefreshCw size={15} />Refresh fields
              </button>
              {requestFieldRefreshStatus && <span className="help-text" role="status">{requestFieldRefreshStatus}</span>}
            </div>
          </div>
          <div className="guide-callout" style={{ marginBottom: 10 }}>
            <strong>Template Variables</strong>
            <div className="help-text" style={{ marginTop: 8 }}>
              Select a request target and variable category, then insert a token.
            </div>
            <div className="workflow-content-segments" role="group" aria-label="Outbound template target" style={{ marginTop: 8 }}>
              {(['pathTemplate', 'bodyTemplate', 'header'] as const).map(target => <button type="button" key={target}
                aria-pressed={templateTarget === target} onClick={() => setTemplateTarget(target)}>
                {target === 'pathTemplate' ? 'Path' : target === 'bodyTemplate' ? 'Body' : 'Header'}
              </button>)}
            </div>
            {templateTarget === 'header' && <div className="form-group" style={{ marginTop: 8 }}>
              <label>Header Name</label>
              <input value={templateHeaderName} onChange={event => setTemplateHeaderName(event.target.value)} placeholder="X-Correlation-Id" />
              {/authorization|api[-_]?key|token|secret|credential/i.test(templateHeaderName) && <p className="help-text">Credential headers are managed through outbound integration secrets.</p>}
            </div>}
            <div className="workflow-content-segments" role="group" aria-label="Template variable category" style={{ marginTop: 8 }}>
              {(['All', 'Common', 'Payload', 'Steps'] as const).map(category => <button type="button" key={category}
                aria-pressed={templateTokenCategory === category} onClick={() => setTemplateTokenCategory(category)}>{category}</button>)}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.45rem', marginTop: 8 }}>
              {visibleActionTokens.map(({ token, label }) => (
                <button key={token} type="button" className="btn-secondary btn-sm"
                  onClick={() => appendActionToken(token)} title={`Insert ${token}`}>
                  {label}
                </button>
              ))}
              {visibleSamplePayloadTokens.map(token => (
                <button key={token} type="button" className="btn-secondary btn-sm"
                  onClick={() => appendActionToken(token)} title={`Insert ${token}`}>
                  {token}
                </button>
              ))}
              {visibleActionTokens.length === 0 && visibleSamplePayloadTokens.length === 0 && <span className="help-text">No variables in this category yet.</span>}
            </div>
          </div>
          <div className="guide-callout" style={{ marginBottom: 10 }}>
            <strong>Schema Assist (Sample Payload)</strong>
            <div className="help-text" style={{ marginTop: 6 }}>
              Paste a representative workflow payload: original input for Direct, or mapped output for Normalise. Extract Tokens exposes paths such as <code>{'{{alert.data.normalisedPayload.details.host}}'}</code>; the compatibility path name is unchanged in both modes.
            </div>
            <textarea
              value={samplePayloadInput}
              onChange={(e) => setSamplePayloadInput(e.target.value)}
              rows={15}
              placeholder={'{\n  "details": "Router unreachable",\n  "device": { "hostname": "edge-fw-01" }\n}'}
              style={{ marginTop: 8, width: '90%', maxWidth: '90%', display: 'block' }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: 8 }}>
              <button type="button" className="btn-secondary btn-sm" onClick={handleSamplePayloadParse}>Extract Tokens</button>
              <span className="help-text">{samplePayloadTokens.length} token(s) found</span>
            </div>
            {samplePayloadError && <div className="error-message" style={{ marginTop: '0.5rem' }}>{samplePayloadError}</div>}
            {samplePayloadTokens.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.45rem', marginTop: 8 }}>
                {samplePayloadTokens.map((token) => (
                  <button key={token} type="button" className="btn-secondary btn-sm" onClick={() => appendToBodyTemplate(token)}>
                    {token}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="guide-callout" style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem' }}>
              <strong>Request Field Mapping</strong>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.45rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={mappingEditorMode === 'expert'}
                  onChange={(event) => setMappingEditorMode(event.target.checked ? 'expert' : 'visual')} />
                Expert JSON
              </label>
            </div>
            <p className="help-text">Map incoming variables or static text to request fields. Join ordered sources with a separator preset, remove mappings, and mark required targets in the dropzone.</p>
            <p className="help-text" role="status">{activeNormalizedPayloadStatus}</p>
            {mappingEditorMode === 'expert' ? (
              <>
                <textarea
                  value={form.requestMappingsJson || emptyRequestMappingsJson()}
                  onChange={event => setForm(previous => ({ ...previous, requestMappingsJson: event.target.value }))}
                  rows={8}
                  style={{ marginTop: 8 }}
                  placeholder={'{\n  "version": 1,\n  "mappings": { "/title": { "sources": ["alert.title", "alert.description"], "combine": "join", "separator": " " } },\n  "requiredTargets": ["/title"]\n}'}
                />
                <p className="help-text" style={{ marginTop: 6 }}>The versioned mapping document keeps direct, join, static, and required-target rules together.</p>
                {requestMappingValidation.error && <p className="error-message" role="alert">{requestMappingValidation.error}</p>}
              </>
            ) : (
              <OutboundActionFieldMapper key={requestFieldRefreshKey} requestSchemaJson={form.requestSchemaJson || ''}
                requestMappingsJson={form.requestMappingsJson || emptyRequestMappingsJson()} samplePayloadTokens={[...samplePayloadTokens, ...activeNormalizedPayloadTokens]}
                integrationVariables={integrationVariableHints}
                activePayloadVariables={activeNormalizedPayloadVariables}
                onChange={requestMappingsJson => setForm(previous => ({ ...previous, requestMappingsJson }))} />
            )}
          </div>
          <div className="guide-callout outbound-mapped-request-preview">
            <strong>Mapped Request Preview</strong>
            <p className="help-text" style={{ marginTop: 6 }}>Shows only selected request output fields, using sample variable values and applied static strings.</p>
            <pre className="hiw-pre" style={{ marginTop: 8 }}>{bodyTemplatePreview || '(empty body template)'}</pre>
          </div>
            </div>}
          </section>
          <section className="outbound-action-section-card" aria-labelledby="outbound-response-heading">
            <div className="outbound-action-section-header">
              <h5 id="outbound-response-heading">Response</h5>
              <button type="button" className="outbound-action-section-toggle"
                aria-expanded={responseSectionOpen} aria-controls="outbound-response-content"
                onClick={() => setResponseSectionOpen(open => !open)}>
                {responseSectionOpen ? 'Hide Response' : 'Show Response'}
                <ChevronDown size={16} aria-hidden="true" />
              </button>
            </div>
            {responseSectionOpen && <div id="outbound-response-content" className="outbound-action-section-body">
          <div className="form-group">
            <label>Ticket ID Variable (optional)</label>
            <input
              value={form.ticketIdVariable || ''}
              onChange={e => setForm({ ...form, ticketIdVariable: e.target.value })}
              placeholder="ticket.id or data.ticket_number"
            />
            <p className="help-text" style={{ marginTop: 6 }}>
              The single source for the ticket association and <code>{'{{integration.ticketId}}'}</code>. If empty, common fields are auto-parsed (including <code>ticket_number</code>); do not also map the same response field to this variable.
            </p>
          </div>
          <div className="form-group">
            <div className="outbound-action-schema-header">
              <label htmlFor="outbound-response-schema">Outbound Response Schema (JSON Schema)</label>
              <div className="outbound-action-schema-buttons">
                <button type="button" className="btn-secondary btn-sm" aria-label="Format response schema JSON" title="Format JSON"
                  onClick={() => {
                    try {
                      setForm(previous => ({ ...previous, responseSchemaJson: formatJsonDocument(previous.responseSchemaJson || '') }));
                      setResponseSchemaError(null);
                    } catch {
                      setResponseSchemaError('Outbound response schema must be valid JSON before it can be formatted.');
                    }
                  }}>
                  <FileJson size={15} />Format JSON
                </button>
                <button type="button" className="btn-secondary btn-sm" aria-label="Refresh response mapping fields" title="Refresh response fields"
                  onClick={refreshResponseFields}>
                  <RefreshCw size={15} />Refresh fields
                </button>
              </div>
            </div>
            <textarea id="outbound-response-schema" value={form.responseSchemaJson || ''} onChange={event => {
              const value = event.target.value;
              setForm(previous => ({ ...previous, responseSchemaJson: value }));
              setResponseFieldRefreshStatus(null);
              if (!value.trim()) setResponseSchemaError(null);
              else {
                try {
                  const parsed = JSON.parse(value);
                  setResponseSchemaError(parsed && typeof parsed === 'object' && !Array.isArray(parsed)
                    ? null : 'Outbound response schema must be a JSON object.');
                } catch {
                  setResponseSchemaError('Outbound response schema must be valid JSON.');
                }
              }
            }} rows={6} placeholder={'{\n  "type": "object",\n  "properties": {\n    "id": { "type": "string" }\n  }\n}'} />
            <p className="help-text" style={{ marginTop: 6 }}>After pasting or editing a response schema, choose Refresh fields to rebuild response sources and destinations. Save Action persists the schema; response body and status are also retained on the workflow step.</p>
            {responseSchemaError && <p className="error-message" role="alert">{responseSchemaError}</p>}
            <details className="outbound-response-sample-generator">
              <summary>Generate schema from sample response</summary>
              <textarea aria-label="Sample response JSON" value={sampleResponseJson}
                onChange={event => { setSampleResponseJson(event.target.value); setSampleResponseError(null); }}
                rows={7} placeholder={'{\n  "ticket_number": 10,\n  "status": "open",\n  "custom_fields": {\n    "cf_employee_id": 2022542\n  }\n}'} />
              <button type="button" className="btn-secondary btn-sm" onClick={generateResponseSchemaFromSample}>
                <FileJson size={15} />Generate schema and refresh fields
              </button>
              {sampleResponseError && <p className="error-message" role="alert">{sampleResponseError}</p>}
            </details>
            {responseFieldRefreshStatus && <p className="help-text" role="status">{responseFieldRefreshStatus}</p>}
          </div>
          <div className="guide-callout" style={{ marginBottom: 10 }}>
            <strong>Response Field Mapping</strong>
            <p className="help-text" style={{ marginTop: 6 }}>
              Use Create variable to name a response payload alias (for example <code>D365TKT</code>) and map it to source <code>/</code>. Ticket associations are handled separately by Ticket ID Variable.
            </p>
            {mappingEditorMode === 'expert' ? (
              <>
                <textarea
                  value={form.responseMappingsJson || '{}'}
                  onChange={event => setForm(previous => ({ ...previous, responseMappingsJson: event.target.value }))}
                  rows={8}
                  style={{ marginTop: 8 }}
                  placeholder={'{\n  "D365TKT": "/"\n}'}
                />
                <p className="help-text" style={{ marginTop: 6 }}>Expert mode uses JSON object syntax: <code>{'{"destination.variable":"/response/path"}'}</code>.</p>
                {responseMappingValidation.error && <p className="error-message" role="alert">{responseMappingValidation.error}</p>}
              </>
            ) : (
              <OutboundActionResponseMapper key={responseFieldRefreshKey} responseSchemaJson={form.responseSchemaJson || ''}
                responseMappingsJson={form.responseMappingsJson || '{}'}
                onChange={responseMappingsJson => setForm(previous => ({ ...previous, responseMappingsJson }))} />
            )}
          </div>
            </div>}
          </section>
          <section className="outbound-action-section-card" aria-labelledby="outbound-post-processing-heading">
            <div className="outbound-action-section-header">
              <h5 id="outbound-post-processing-heading">Post-processing</h5>
              <button type="button" className="outbound-action-section-toggle"
                aria-expanded={postProcessingSectionOpen} aria-controls="outbound-post-processing-content"
                onClick={() => setPostProcessingSectionOpen(open => !open)}>
                {postProcessingSectionOpen ? 'Hide Post-processing' : 'Show Post-processing'}
                <ChevronDown size={16} aria-hidden="true" />
              </button>
            </div>
            {postProcessingSectionOpen && <div id="outbound-post-processing-content" className="outbound-action-section-body">
          <div className="guide-callout" style={{ marginBottom: 10 }}>
            <strong>Mapping Troubleshooting</strong>
            <div className="help-text" style={{ marginTop: 6 }}>
              Template tokens detected: <strong>{actionTokens.length}</strong>
            </div>
            <div className="help-text" style={{ marginTop: 6 }}>
              Integration field tokens: <strong>{integrationFieldTokens.length}</strong>
            </div>
            {integrationFieldTokens.length > 0 && (
              <div className="help-text" style={{ marginTop: 6 }}>
                These fields are resolved by workflow step mappings: <code>{integrationFieldTokens.join(', ')}</code>
              </div>
            )}
            {requiresAssociatedTicketId && !usesAssociatedTicketIdToken && (
              <div className="error-message" style={{ marginTop: 8 }}>
                Lifecycle action should reference <code>{'{{integration.ticketId}}'}</code> in path/body/headers.
              </div>
            )}
            {form.actionKey === 'createTicket' && !(form.ticketIdVariable || '').trim() && (
              <div className="help-text" style={{ marginTop: 8 }}>
                Ticket ID Variable is optional. Auto-parse fallback is enabled when left blank.
              </div>
            )}
            <div className="help-text" style={{ marginTop: 8 }}>
              Mapping rules configured: <strong>{mappingRuleCount}</strong>
            </div>
            <div className="help-text" style={{ marginTop: 6 }}>
              Request mappings: <strong style={{ color: requestMappingValidation.error ? 'var(--color-severity-critical)' : 'inherit' }}>{requestMappingValidation.error ? 'invalid' : `${requestMappingValidation.count} valid`}</strong>
              {' '}| Response mappings: <strong style={{ color: responseMappingValidation.error ? 'var(--color-severity-critical)' : 'inherit' }}>{responseMappingValidation.error ? 'invalid' : `${responseMappingValidation.count} valid`}</strong>
            </div>
            {(requestMappingValidation.error || responseMappingValidation.error) && (
              <div className="error-message" style={{ marginTop: 8 }}>
                {requestMappingValidation.error || responseMappingValidation.error}
              </div>
            )}
          </div>
          <div className="guide-callout" style={{ marginBottom: 10 }}>
            <strong>Live Test</strong>
            <div className="help-text" style={{ marginTop: 6 }}>
              Runs this saved action through relay-backed execution using the integration's configured auth.
            </div>
            <textarea
              value={testVariablesInput}
              onChange={(e) => setTestVariablesInput(e.target.value)}
              rows={6}
              placeholder={'{\n  "alert.title": "Router Down",\n  "alert.description": "WAN gateway unreachable"\n}'}
              style={{ marginTop: 8, width: '90%', maxWidth: '90%', display: 'block' }}
            />
            <div style={{ marginTop: 8, display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <button type="button" className="btn-secondary btn-sm" onClick={runLiveTest} disabled={testRunning || !editingActionId || draftIsDirty}>
                {testRunning ? 'Testing...' : 'Run Live Test'}
              </button>
              {!editingActionId && <span className="help-text">Save action first to enable live test.</span>}
              {editingActionId && draftIsDirty && <span className="help-text">Save draft changes to test the latest action payload.</span>}
            </div>
            {testError && <div className="error-message" style={{ marginTop: 8 }}>{testError}</div>}
            {testResult && (
              <div style={{ marginTop: 8 }}>
                <div className="help-text">
                  Status: <strong>{testResult.statusCode}</strong> | Success: <strong>{String(testResult.success)}</strong> | Latency: <strong>{testResult.latencyMs}ms</strong>
                </div>
                {testResult.errorCode && (
                  <div className="help-text" style={{ marginTop: 4 }}>
                    Error: <code>{testResult.errorCode}</code>{testResult.errorMessage ? ` - ${testResult.errorMessage}` : ''}
                  </div>
                )}
                <pre className="hiw-pre" style={{ marginTop: 8 }}>
{testResult.responseBodyPreview || '(empty response body)'}
                </pre>
                {Array.isArray(testResult.mappingCandidates) && testResult.mappingCandidates.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <div className="help-text" style={{ marginBottom: 6 }}>
                      Mapping candidates (JSONPath):
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                      {testResult.mappingCandidates.slice(0, 24).map((candidate: string) => (
                        <button
                          key={candidate}
                          type="button"
                          className="btn-secondary btn-sm"
                          onClick={() => setForm((prev) => ({ ...prev, ticketIdVariable: candidate }))}
                          title="Set as Ticket ID Variable"
                        >
                          {candidate}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="form-row">
            <div className="form-group" style={{ flex: 0.4 }}>
              <label>Sort Order</label>
              <input type="number" value={form.sortOrder || 0} onChange={e => setForm({ ...form, sortOrder: parseInt(e.target.value || '0', 10) })} />
            </div>
            <div className="form-group" style={{ flex: 0.6, display: 'flex', alignItems: 'flex-end' }}>
              <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input type="checkbox" checked={form.isEnabled ?? true} onChange={e => setForm({ ...form, isEnabled: e.target.checked })} />
                Enabled
              </label>
            </div>
          </div>
            </div>}
          </section>
          </div>
          <div className="form-actions outbound-action-cabinet-actions">
            <button type="button" className="btn-secondary" onClick={resetForm}>{editingActionId ? 'Cancel Edit' : 'Cancel Draft'}</button>
            <button type="submit" className="btn-primary" disabled={submitting || !form.name.trim() || !draftIsDirty}>
              {submitting ? 'Saving...' : editingActionId ? 'Save Action' : 'Add Action'}
            </button>
          </div>
            </form>}
          </main>
        </div>
        {pendingActionConfirm && (
          <div className="modal-overlay" onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeActionPrompt();
          }}>
            <div className="modal modal-sm" role="alertdialog" aria-modal="true" aria-labelledby="outbound-action-confirm-title"
              onClick={event => event.stopPropagation()}>
              <h3 id="outbound-action-confirm-title">{resolveActionConfirmCopy(pendingActionConfirm).title}</h3>
              <p>{resolveActionConfirmCopy(pendingActionConfirm).message}</p>
              <div className="form-actions">
                <button type="button" className="btn-secondary" disabled={actionConfirmSubmitting} onClick={closeActionPrompt}>Cancel</button>
                <button type="button" className={resolveActionConfirmCopy(pendingActionConfirm).confirmClassName}
                  disabled={actionConfirmSubmitting} onClick={() => void confirmActionPrompt()}>
                  {actionConfirmSubmitting ? 'Working…' : resolveActionConfirmCopy(pendingActionConfirm).confirmLabel}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const __outboundActionsModalTesting = { OutboundActionsModal };

const IntegrationActionsHowItWorks: React.FC = () => {
  const [open, setOpen] = useState(false);

  return (
    <div className="hiw-panel" style={{ marginBottom: '1rem' }}>
      <button className="hiw-toggle" onClick={() => setOpen((v) => !v)} aria-expanded={open} type="button">
        <span className="hiw-icon">&#x2139;&#xFE0F;</span>
        <span className="hiw-title">How do Integration Actions work?</span>
        <span className="hiw-chevron">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="hiw-body">
          <div className="hiw-section">
            <h3 className="hiw-section-title">What they are</h3>
            <p className="hiw-text">
              Integration Actions are reusable HTTP action presets for an outbound integration.
              Workflows reference these actions from <strong>Integration Action</strong> steps so
              request structure stays centralized and consistent.
            </p>
          </div>

          <div className="hiw-section">
            <h3 className="hiw-section-title">Configure in 4 steps</h3>
            <ol className="hiw-steps">
              <li>
                <span className="hiw-step-num">1</span>
                <div>
                  <strong>Create the action</strong>
                  <p>Set a descriptive name, action key, HTTP method, and path template.</p>
                </div>
              </li>
              <li>
                <span className="hiw-step-num">2</span>
                <div>
                  <strong>Build request templates</strong>
                  <p>Use tokens like <code>{'{{alert.title}}'}</code>, <code>{'{{alert.data.*}}'}</code>, and <code>{'{{integration.ticketId}}'}</code> in path/body/headers.</p>
                </div>
              </li>
              <li>
                <span className="hiw-step-num">3</span>
                <div>
                  <strong>Use Schema Assist and preview</strong>
                  <p>Paste sample inbound payload JSON to generate <code>alert.data.*</code> token suggestions and verify rendered output.</p>
                </div>
              </li>
              <li>
                <span className="hiw-step-num">4</span>
                <div>
                  <strong>Map ticket IDs for lifecycle actions</strong>
                  <p>For <code>createTicket</code>, set <em>Ticket ID Variable</em> (for example <code>ticket.id</code>) so update/close actions can reuse it. If blank, common fields are auto-detected.</p>
                </div>
              </li>
            </ol>
          </div>

          <div className="hiw-section">
            <h3 className="hiw-section-title">How workflows use these actions</h3>
            <p className="hiw-text">
              In Workflow Builder, add an <strong>Integration Action</strong> step and select the
              integration plus action preset. Keep payload templates here in Integration Actions
              rather than inside workflow steps.
            </p>
            <p className="hiw-text">Configured routing and workflows decide when actions run. Saving an action or enabling an outbound integration does not automatically send every event. Normalised Output is an internal inbound contract, not an outbound request.</p>
          </div>
        </div>
      )}
    </div>
  );
};

const SimpleKeyValueEditor: React.FC<{
  value: Record<string, string>;
  onChange: (value: Record<string, string>) => void;
}> = ({ value, onChange }) => {
  const entries = Object.entries(value);

  const setEntry = (oldKey: string, newKey: string, newValue: string) => {
    const next: Record<string, string> = {};
    entries.forEach(([k, v]) => {
      if (k === oldKey) return;
      next[k] = v;
    });
    next[newKey] = newValue;
    onChange(next);
  };

  const remove = (key: string) => {
    const { [key]: _, ...rest } = value;
    onChange(rest);
  };

  const add = () => {
    let key = 'header';
    let i = 1;
    while (value[key]) {
      key = `header-${i++}`;
    }
    onChange({ ...value, [key]: '' });
  };

  return (
    <div>
      {entries.map(([key, val]) => (
        <div key={key} className="form-row" style={{ alignItems: 'center' }}>
          <div className="form-group" style={{ flex: 1 }}>
            <input value={key} onChange={e => setEntry(key, e.target.value, val)} />
          </div>
          <div className="form-group" style={{ flex: 2 }}>
            <input value={val} onChange={e => setEntry(key, key, e.target.value)} />
          </div>
          <button type="button" className="btn-icon" onClick={() => remove(key)}>X</button>
        </div>
      ))}
      <button type="button" className="btn-secondary btn-sm" onClick={add}>+ Add Header</button>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// How It Works — Inbound
// ══════════════════════════════════════════════════════════════════════════════

export const InboundHowItWorks: React.FC<{ open?: boolean; hideToggle?: boolean }> = ({ open, hideToggle = false }) => {
  const [localOpen, setLocalOpen] = useState(false);
  const expanded = open ?? localOpen;
  const [helpOpen, setHelpOpen] = useState(false);
  if (hideToggle && !expanded) {
    return null;
  }

  return (
    <div className="hiw-panel">
      {!hideToggle && <button className="hiw-toggle" onClick={() => setLocalOpen(v => !v)} aria-expanded={expanded}>
        <span className="hiw-icon">&#x2139;&#xFE0F;</span>
        <span className="hiw-title">How do Inbound Integrations work?</span>
        <span className="hiw-chevron">{expanded ? '▲' : '▼'}</span>
      </button>}

      {expanded && (
        <div className="hiw-body">
          {/* What are they */}
          <div className="hiw-section">
            <h3 className="hiw-section-title">What are Inbound Integrations?</h3>
            <p className="hiw-text">
              Inbound integrations receive <strong>Alert and Notification</strong> payloads through a
              secure HTTP webhook. <strong>Direct</strong> recognises one selected input schema and
              preserves the complete incoming object. <strong>Normalise</strong> maps recognised input
              schemas to a common saved output contract. Configured routing,
              escalation policies and workflows determine subsequent processing.
            </p>
            <p className="hiw-text">
              Each inbound integration gets its own API key, so you can have one integration per tool,
              per environment, or per team — and revoke keys individually without affecting anything else.
            </p>
          </div>

          {/* Step-by-step */}
          <div className="hiw-section">
            <h3 className="hiw-section-title">Setup</h3>
            <ol className="hiw-steps">
              <li>
                <span className="hiw-step-num">1</span>
                <div>
                  <strong>Create an inbound integration</strong>
                  <p>Click <em>+ Add Inbound</em>, give it a name, and choose the provider that matches your
                  source. Choose Alert for lifecycle processing or Notification for notification workflows.
                  Use <em>Generic Webhook</em> if your tool is not listed. Provider presets are starting points;
                  validate the actual payload before enabling intake.</p>
                </div>
              </li>
              <li>
                <span className="hiw-step-num">2</span>
                <div><strong>Choose payload handling and review the input</strong>
                  <p>Direct is the default for new integrations, with Strict on and the integration disabled. Open Schemas,
                  select the input schema, and review its Direct runtime bindings. Use Example payload and Infer schema
                  when defining a new input, then Preview and Save schema.</p>
                  <p>For Normalise, save Normalised Output and its runtime bindings, then save each input mapping.
                  Save publishes an immutable snapshot automatically. Save mode applies a staged mode change after validation;
                  Cancel mode discards it. Switching retains inactive Normalise output, mappings and bindings, and never rewrites historical receipts.</p>
                </div>
              </li>
              <li>
                <span className="hiw-step-num">3</span>
                <div>
                  <strong>Generate an API key</strong>
                  <p>Click <em>Keys</em> on the integration row, then <em>Generate New Key</em>. Copy the
                  key immediately — it is shown only once.</p>
                </div>
              </li>
              <li>
                <span className="hiw-step-num">4</span>
                <div>
                  <strong>Configure your source</strong>
                  <p>In your tool's webhook settings, set the destination URL and add the authentication header:</p>
                  <div className="hiw-code-row">
                    <div className="hiw-code-block">
                      <span className="hiw-code-label">Webhook URL</span>
                      <code>{INGESTION_ENDPOINT}</code>
                    </div>
                    <div className="hiw-code-block">
                      <span className="hiw-code-label">Auth header</span>
                      <code>{INGESTION_API_KEY_HEADER}: &lt;your-api-key&gt;</code>
                    </div>
                  </div>
                </div>
              </li>
              <li>
                <span className="hiw-step-num">5</span>
                <div>
                  <strong>Enable the schema and integration</strong>
                  <p>After a successful Preview, enable the selected schema using Enabled in the schema footer.
                  Keep Strict on for registered processing, then enable the integration in the integration list.
                  Schema Enabled and integration Enabled are separate controls; saving alone does not start intake.</p>
                </div>
              </li>
            </ol>
          </div>

          {/* Payload transformation */}
          <div className="hiw-section">
            <h3 className="hiw-section-title">Payload Handling And Workflow Fields</h3>
            <p className="hiw-text">
              Direct does not map, wrap or discard fields. Its runtime bindings read the incoming object to obtain
              event identity, severity, status and other required runtime values. Normalise applies source pointers,
              literals, defaults, conversions and value mappings; its runtime bindings read the resulting saved output.
            </p>
            <p className="hiw-text">
              Workflow fields under <code>data.normalisedPayload</code> keep their compatibility name in both modes:
              they contain the unchanged input in Direct and the mapped output in Normalise. Use the matching
              <code> alert.data.normalisedPayload</code> or <code> notification.data.normalisedPayload</code> source.
              Field pickers use the effective enabled contract, not inactive Normalise settings retained during Direct processing.
            </p>
          </div>
          <div className="hiw-section">
            <h3 className="hiw-section-title">Preview And Quarantine</h3>
            <p className="hiw-text">
              Preview checks recognition, payload output and runtime bindings without sending an event.
              With Strict on, unrecognised or invalid inputs are quarantined rather than sent through the legacy path.
              Review Receipts to diagnose the saved payload and snapshot. Controlled release is an explicit action that creates
              a linked child receipt; saving a schema or switching modes does not release or resend historical events.
            </p>
          </div>
          <div className="hiw-section">
            <h3 className="hiw-section-title">Legacy Processing (Strict Off)</h3>
            <p className="hiw-text">
              Direct preserves the complete incoming workflow payload with Strict on or off. With Strict off,
              legacy processing still extracts Alert lifecycle metadata; it does not replace the Direct workflow payload.
              Existing integrations retain their saved mode and Strict state; the new Direct default does not migrate them.
              Legacy transformation settings are separate from registered schemas:
            </p>
            <div className="hiw-cards">
              <div className="hiw-card">
                <div className="hiw-card-header">Default</div>
                <p>Legacy provider-specific handling is available for supported formats. Selecting a provider does not guarantee automatic compatibility with every payload. Review and preview registered schemas for your actual inputs.</p>
              </div>
              <div className="hiw-card">
                <div className="hiw-card-header">JSONata Expression</div>
                <p>The legacy expression field is not executed by the current runtime. Use the registered mapping wizard or Expert JSON rules instead; they are not a JSONata expression engine.</p>
              </div>
              <div className="hiw-card">
                <div className="hiw-card-header">Field Mappings (JSON)</div>
                <p>Legacy field mappings are distinct from registered schema mapping rules. For registered schemas use the saved-output mapping wizard with JSON Pointers and validation.</p>
                <pre className="hiw-pre">{`{\n  "title": "alert_name",\n  "severity": "priority"\n}`}</pre>
              </div>
            </div>
          </div>
          <button type="button" className="btn-secondary" onClick={() => setHelpOpen(true)}><CircleHelp size={16} />Open schema guide</button>
        </div>
      )}
      {helpOpen && <InboundSchemaGuide onClose={() => setHelpOpen(false)} />}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// How It Works — Outbound
// ══════════════════════════════════════════════════════════════════════════════

export const OutboundHowItWorks: React.FC<{ open?: boolean; hideToggle?: boolean }> = ({ open, hideToggle = false }) => {
  const [localOpen, setLocalOpen] = useState(false);
  const expanded = open ?? localOpen;
  if (hideToggle && !expanded) {
    return null;
  }

  return (
    <div className="hiw-panel">
      {!hideToggle && <button className="hiw-toggle" onClick={() => setLocalOpen(v => !v)} aria-expanded={expanded}>
        <span className="hiw-icon">&#x2139;&#xFE0F;</span>
        <span className="hiw-title">How do Outbound Integrations work?</span>
        <span className="hiw-chevron">{expanded ? '▲' : '▼'}</span>
      </button>}

      {expanded && (
        <div className="hiw-body">
          {/* What are they */}
          <div className="hiw-section">
            <h3 className="hiw-section-title">What are Outbound Integrations?</h3>
            <p className="hiw-text">
              Outbound integrations define a destination, authentication and reusable HTTP actions.
              Workflow <strong>Integration Action</strong> steps map Alert, Notification or prior-step values into
              an action request and use its response in later steps. A provider preset supplies editable defaults,
              not guaranteed compatibility with every API version. Enabling a destination alone does not send events.
            </p>
            <p className="hiw-text">
              Common use-cases include auto-creating Jira tickets for critical alerts, posting to a
              Slack channel when an alert is acknowledged, or syncing status to a ServiceNow incident.
            </p>
          </div>

          {/* Step-by-step */}
          <div className="hiw-section">
            <h3 className="hiw-section-title">Setup</h3>
            <ol className="hiw-steps">
              <li>
                <span className="hiw-step-num">1</span>
                <div>
                  <strong>Create an outbound integration</strong>
                  <p>Click <em>+ Add Outbound</em>, name it, choose the provider, and enter the Base URL for the
                  target system (e.g. <code>https://your-org.atlassian.net</code> for Jira).</p>
                </div>
              </li>
              <li>
                <span className="hiw-step-num">2</span>
                <div>
                  <strong>Configure authentication</strong>
                  <p>Choose None, Bearer Token, API Key Header or Basic Auth to match the provider API.
                  Enter the corresponding token, header name/value, or username/password fields and save.
                  Credential values are stored in Azure Key Vault; the integration stores a reference.</p>
                </div>
              </li>
              <li>
                <span className="hiw-step-num">3</span>
                <div>
                  <strong>Define an action and its request</strong>
                  <p>Open Actions, select a preset or add an action, and review its Action Key, Method and Path Template.
                  Enter a Body Template and Outbound Request Schema. Generate schema from Body Template creates a schema
                  from the sample; Refresh fields only rereads the schema already shown.</p>
                  <p>In Request Field Mapping, map source variables or static values to selected provider fields.
                  Expert JSON uses target JSON Pointers and source variable paths. Mapped Request Preview shows the selected
                  output fields with sample values; it does not call the provider.</p>
                </div>
              </li>
              <li>
                <span className="hiw-step-num">4</span>
                <div>
                  <strong>Map the response and ticket identity</strong>
                  <p>Enter an Outbound Response Schema or generate it from a sample response, then Refresh fields.
                  Response Field Mapping maps response JSON Pointers to variables for later steps. Create variable can
                  retain the entire response under an alias by mapping source <code>/</code>.</p>
                  <p>Ticket ID Variable separately selects the response path used for ticket association, such as
                  <code> ticket.id</code>. If blank, common ID fields are auto-detected. Do not duplicate it as a response
                  mapping to <code>integration.ticketId</code>. Update/close actions reuse <code>{'{{integration.ticketId}}'}</code>.</p>
                </div>
              </li>
              <li>
                <span className="hiw-step-num">5</span>
                <div>
                  <strong>Save, test and connect the workflow</strong>
                  <p>Save Action persists the configuration; Revert Draft discards unsaved changes. Run Live Test requires
                  a saved, unchanged action and sends a real request with the configured authentication. Use a loopback stub
                  for local acceptance or an approved test destination; inspect status, response and errors before proceeding.</p>
                  <p>Select the destination and action in a workflow Integration Action step, review its field mappings,
                  and enable the action and destination for the intended route. Check workflow results and the Audit Log
                  for delivery evidence. Saving configuration or enabling a destination is not a send.</p>
                </div>
              </li>
            </ol>
          </div>

          <div className="hiw-section">
            <h3 className="hiw-section-title">Inbound Sources And Mode Changes</h3>
            <p className="hiw-text">
              Source fields come from the effective enabled inbound contract. Direct exposes the complete incoming object;
              Normalise exposes mapped output. Both use <code>data.normalisedPayload</code> paths, with Alert or Notification
              scope as appropriate. Integration-qualified sources identify which inbound integration owns a field.
              A saved mode change can change available fields: review request mappings and preview them again before delivery.
              Retained inactive output is not the current contract, and previously queued events keep their frozen intake snapshot.
            </p>
          </div>

          {/* Auth types */}
          <div className="hiw-section">
            <h3 className="hiw-section-title">Authentication Types</h3>
            <div className="hiw-cards hiw-cards-2">
              <div className="hiw-card">
                <div className="hiw-card-header">None</div>
                <p>No credential is sent. Suitable for internal endpoints or tools that authenticate
                by IP allowlisting or network policy rather than a token.</p>
              </div>
              <div className="hiw-card">
                <div className="hiw-card-header">Bearer Token</div>
                <p>Enter a <strong>Token</strong>. Sends <code>Authorization: Bearer &lt;token&gt;</code>.
                Use this only when the provider endpoint accepts bearer authentication.</p>
              </div>
              <div className="hiw-card">
                <div className="hiw-card-header">API Key Header</div>
                <p>Enter a <strong>Header Name</strong> and <strong>Header Value</strong>. Sends a
                custom HTTP header containing the key. Use the header name required by the provider.</p>
              </div>
              <div className="hiw-card">
                <div className="hiw-card-header">Basic Auth</div>
                <p>Enter a <strong>Username</strong> and <strong>Password</strong>. Sends
                <code>Authorization: Basic &lt;base64&gt;</code>. Widely supported by legacy or
                on-premises systems.</p>
              </div>
            </div>
          </div>

          {/* Secret Reference */}
          <div className="hiw-section">
            <h3 className="hiw-section-title">How credentials are stored</h3>
            <p className="hiw-text">
              Each auth type shows specific typed fields — a token, a username and password, or a
              header name and value. When you save, Zurvit serialises only the relevant fields into
              a JSON blob and stores it in <strong>Azure Key Vault</strong> using its managed
              identity. Only a reference name is saved in the integration database; credential values
              are not ordinary integration fields.
            </p>
            <p className="hiw-text">
              You can also reference secrets inline inside the JSON configuration field using the
              format <code>{'{{secret.my-secret-name}}'}</code> — for example to embed a token
              in a custom request body template.
            </p>
            <div className="hiw-callout">
              To rotate a credential, open Edit, enter the new values and save. Leave credential
              fields blank to keep the existing secret. Do not put credentials in sample payloads,
              request/response mappings or workflow variable aliases.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
